import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Shield, ShieldCheck, Users, Store, Heart, Star, MessageSquare, CheckCircle, XCircle,
  Clock, Search, Activity, Eye, X, Ban, RotateCcw, Mail,
  MapPin, Phone, Calendar, DollarSign, ChevronRight, AlertTriangle, Globe, RefreshCw,
  Image, Upload, Trash2, Edit, Plus, Key, Copy, RefreshCcw, Gavel, Tag, MessageCircle, FileText
} from '../icons';
import { useAuth } from '../context/AuthContext';
import compressImage from '../utils/compressImage';
import { useConfirm, usePrompt } from '../components/Modal';
import mediaUrl from '../utils/mediaUrl';
import api, {
  getAdminStats, getAdminVendors, getAdminVendorDetail, approveVendor, rejectVendor,
  suspendVendor, unsuspendVendor, resetVendorPassword,
  getAdminUsers, getAdminCouples, getAdminCoupleDetail,
  getAdminWeddings, getAdminReviews, getAdminInquiries, getAdminActivity,
  syncGoogleVendors, syncAllGoogleVendors, getGoogleVendorStatus,
  restoreWedding, suspendWedding, unsuspendWedding, adminDeleteWedding,
  testAdminEmail, getAdminSettings, updateAdminSettings,
  getAdminHeroImages, addHeroImage, updateHeroImage, deleteHeroImage,
  getAdminCategories, updateCategoryThumbnail,
  getAdminFamilyRequests, approveFamilyRequest, rejectFamilyRequest,
  bulkVendorAction, getVendorLeads, sendNewsletterEmails,
  getAdminBiddingRequests, approveBiddingRequest, denyBiddingRequest, revokeBiddingAccess,
  adminDeleteVendor, adminDeleteUser, adminDeleteCouple,
  getAdminInvitations,
  markAdminInquiryRead, getInquiryMessages,
  getAdminCompletedWeddings, adminMarkWeddingComplete,
} from '../services/api';

function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [promptDialog, PromptDialog] = usePrompt();
  const initialTab = searchParams.get('tab') || 'vendors';
  const highlightInqId = searchParams.get('inq') ? parseInt(searchParams.get('inq')) : null;
  const highlightRowRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [activity, setActivity] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tabSearch, setTabSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [expandedInqId, setExpandedInqId] = useState(null);
  const [inqMessages, setInqMessages] = useState([]);
  const [inqMsgLoading, setInqMsgLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  // Load failures are shown instead of looking like an empty list
  const [loadError, setLoadError] = useState('');
  const [tabError, setTabError] = useState('');

  // Vendor detail modal
  const [vendorDetail, setVendorDetail] = useState(null);
  const [vendorDetailLoading, setVendorDetailLoading] = useState(false);

  // Couple detail modal
  const [coupleDetail, setCoupleDetail] = useState(null);
  const [coupleDetailLoading, setCoupleDetailLoading] = useState(false);

  // Tab data
  const [usersList, setUsersList] = useState([]);
  const [couplesList, setCouplesList] = useState([]);
  const [weddingsList, setWeddingsList] = useState([]);
  const [completedWeddings, setCompletedWeddings] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [reviewsList, setReviewsList] = useState([]);
  const [inquiriesList, setInquiriesList] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);

  // Mailbox unread badge
  const [mailboxUnread, setMailboxUnread] = useState(0);

  // Google sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);

  // Categories state
  const [categoriesList, setCategoriesList] = useState([]);

  // Family requests state
  const [familyRequests, setFamilyRequests] = useState([]);

  // Multi-select vendors
  const [selectedVendorIds, setSelectedVendorIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Bidding requests
  const [biddingRequests, setBiddingRequests] = useState([]);
  const [biddingActionLoading, setBiddingActionLoading] = useState(null);

  // Admin Invitations
  const [adminInvitations, setAdminInvitations] = useState([]);
  const [adminInvitationStats, setAdminInvitationStats] = useState({});

  // Newsletter / Vendor Leads
  const [vendorLeads, setVendorLeads] = useState([]);
  const [vendorLeadsEmailCount, setVendorLeadsEmailCount] = useState(0);
  const [leadsFilter, setLeadsFilter] = useState('');
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterBody, setNewsletterBody] = useState('');
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [newsletterResult, setNewsletterResult] = useState(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [emailOnly, setEmailOnly] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    loadData().then(() => {
      if (initialTab !== 'vendors') loadTabData(initialTab);
    });
    // Mailbox unread count, through the shared client so the API base is used
    // (a relative /api URL fails in the Android app). A missing badge is harmless.
    api.get('/email-admin/stats')
      .then(res => setMailboxUnread(parseInt(res.data?.unread || 0))).catch(() => {});
  }, [user, navigate, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand & scroll to highlighted inquiry (from notification click)
  useEffect(() => {
    if (highlightInqId && inquiriesList.length > 0) {
      const inq = inquiriesList.find(i => i.id === highlightInqId);
      if (inq) {
        // Auto-expand the inquiry
        setExpandedInqId(highlightInqId);
        setInqMsgLoading(true);
        getInquiryMessages(highlightInqId).then(res => {
          setInqMessages(res.data || []);
          if (!inq.admin_read) {
            markAdminInquiryRead(highlightInqId).catch(() => {});
            setInquiriesList(prev => prev.map(x => x.id === highlightInqId ? { ...x, admin_read: true } : x));
          }
        }).catch(() => setInqMessages([])).finally(() => setInqMsgLoading(false));
        setTimeout(() => highlightRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
      }
    }
  }, [highlightInqId, inquiriesList]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [statsRes, vendorsRes, activityRes] = await Promise.all([
        getAdminStats(),
        getAdminVendors({}),
        getAdminActivity(),
      ]);
      setStats(statsRes.data);
      setVendors(vendorsRes.data);
      setActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to load admin data', err);
      setLoadError('Could not load the dashboard data: ' + (err.response?.data?.error || err.message) + '. Refresh the page to try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const res = await getAdminVendors(params);
      setVendors(res.data);
    } catch (err) {
      console.error('Failed to load vendors', err);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    if (!loading) loadVendors();
  }, [statusFilter, searchQuery, loading, loadVendors]);

  // Load tab data on tab switch
  const loadTabData = async (tab) => {
    setTabLoading(true);
    setTabError('');
    try {
      if (tab === 'users') { const res = await getAdminUsers({}); setUsersList(res.data); }
      else if (tab === 'couples') { const res = await getAdminCouples(); setCouplesList(res.data); }
      else if (tab === 'weddings') { const res = await getAdminWeddings(); setWeddingsList(res.data); }
      else if (tab === 'completed-weddings') {
        setCompletedLoading(true);
        try { const res = await getAdminCompletedWeddings(); setCompletedWeddings(res.data); } finally { setCompletedLoading(false); }
      }
      else if (tab === 'reviews') { const res = await getAdminReviews(); setReviewsList(res.data); }
      else if (tab === 'inquiries') { const res = await getAdminInquiries(); setInquiriesList(res.data); }
      else if (tab === 'categories') { const res = await getAdminCategories(); setCategoriesList(res.data); }
      else if (tab === 'family-requests') { const res = await getAdminFamilyRequests(); setFamilyRequests(res.data); }
      else if (tab === 'newsletter') { const res = await getVendorLeads({ verified_only: verifiedOnly }); setVendorLeads(res.data.vendors || res.data); setVendorLeadsEmailCount(res.data.withEmail || 0); }
      else if (tab === 'bidding-requests') { const res = await getAdminBiddingRequests(); setBiddingRequests(res.data); }
      else if (tab === 'invitations') { const res = await getAdminInvitations(); setAdminInvitations(res.data.invitations || []); setAdminInvitationStats(res.data.stats || {}); }
    } catch (err) {
      console.error('Failed to load tab data', err);
      setTabError('Could not load this tab: ' + (err.response?.data?.error || err.message) + '. Open the tab again to retry.');
    } finally {
      setTabLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTabSearch('');
    setTabError('');
    if (['users', 'couples', 'weddings', 'completed-weddings', 'reviews', 'inquiries', 'categories', 'family-requests', 'newsletter', 'bidding-requests', 'invitations'].includes(tab)) {
      loadTabData(tab);
    }
  };

  // Client-side search filter for non-vendor tabs
  const matchesSearch = (item, fields) => {
    if (!tabSearch) return true;
    const q = tabSearch.toLowerCase();
    return fields.some(f => {
      const val = typeof f === 'function' ? f(item) : item[f];
      return val && String(val).toLowerCase().includes(q);
    });
  };

  const loadGoogleStatus = async () => {
    try {
      const res = await getGoogleVendorStatus();
      setSyncStatus(res.data);
    } catch (err) {
      console.error('Failed to load Google sync status', err);
    }
  };

  const handleSyncAll = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await syncAllGoogleVendors();
      setSyncResult(res.data);
      loadGoogleStatus();
      loadVendors();
    } catch (err) {
      setSyncResult({ error: err.response?.data?.error || 'Sync failed. Check server logs and ensure GOOGLE_PLACES_API_KEY is set in the server .env file.' });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      const res = await approveVendor(id);
      // Approval emails the vendor a new temporary password
      if (res.data?.emailSent === false) {
        alert('Vendor approved, but the login email could not be sent. Open the vendor (eye icon) to copy the temporary password and pass it on.');
      }
      await loadVendors();
      const statsRes = await getAdminStats();
      setStats(statsRes.data);
    } catch (err) {
      alert('Failed to approve vendor: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    if (!await confirmDialog({ title: 'Reject Vendor', message: 'Are you sure you want to reject this vendor?', danger: true, confirmText: 'Reject' })) return;
    setActionLoading(id);
    try {
      await rejectVendor(id);
      await loadVendors();
      const statsRes = await getAdminStats();
      setStats(statsRes.data);
    } catch (err) {
      alert('Failed to reject vendor');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (id) => {
    if (!await confirmDialog({ title: 'Suspend Vendor', message: 'Are you sure you want to suspend this vendor? They will not be able to log in.', danger: true, confirmText: 'Suspend' })) return;
    setActionLoading(id);
    try {
      await suspendVendor(id);
      await loadVendors();
      const statsRes = await getAdminStats();
      setStats(statsRes.data);
    } catch (err) {
      alert('Failed to suspend vendor');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnsuspend = async (id) => {
    if (!await confirmDialog({ title: 'Unsuspend Vendor', message: 'Unsuspend and re-approve this vendor?' })) return;
    setActionLoading(id);
    try {
      await unsuspendVendor(id);
      await loadVendors();
      const statsRes = await getAdminStats();
      setStats(statsRes.data);
    } catch (err) {
      alert('Failed to unsuspend vendor');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewVendor = async (id) => {
    setVendorDetailLoading(true);
    try {
      const res = await getAdminVendorDetail(id);
      setVendorDetail(res.data);
    } catch (err) {
      alert('Failed to load vendor details');
    } finally {
      setVendorDetailLoading(false);
    }
  };

  const handleViewCouple = async (id) => {
    setCoupleDetailLoading(true);
    try {
      const res = await getAdminCoupleDetail(id);
      setCoupleDetail(res.data);
    } catch (err) {
      alert('Failed to load couple details');
    } finally {
      setCoupleDetailLoading(false);
    }
  };

  // Stat card click handlers
  const handleStatClick = (type) => {
    switch (type) {
      case 'users': handleTabChange('users'); break;
      case 'vendors': setStatusFilter(''); handleTabChange('vendors'); break;
      case 'pending': setStatusFilter('pending'); handleTabChange('vendors'); break;
      case 'weddings': handleTabChange('weddings'); break;
      case 'reviews': handleTabChange('reviews'); break;
      case 'inquiries': handleTabChange('inquiries'); break;
      default: break;
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
  const formatNPR = (n) => n ? `NPR ${Number(n).toLocaleString()}` : 'N/A';

  // Bulk vendor selection
  const toggleVendorSelect = (id) => {
    setSelectedVendorIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedVendorIds(prev => prev.size === vendors.length ? new Set() : new Set(vendors.map(v => v.id)));
  };
  const handleBulkApprove = async () => {
    if (!selectedVendorIds.size || !await confirmDialog({ title: 'Bulk Approve', message: `Approve ${selectedVendorIds.size} vendor(s)? They are marked verified and keep their current password. Unlike the single Approve button, no login email is sent.`, confirmText: 'Approve All' })) return;
    setBulkLoading(true);
    try {
      await bulkVendorAction({ ids: [...selectedVendorIds], action: 'approve' });
      setSelectedVendorIds(new Set());
      await loadVendors();
      setStats((await getAdminStats()).data);
    } catch { alert('Bulk approve failed'); }
    finally { setBulkLoading(false); }
  };
  const handleBulkReject = async () => {
    if (!selectedVendorIds.size || !await confirmDialog({ title: 'Bulk Reject', message: `Reject ${selectedVendorIds.size} vendor(s)?`, danger: true, confirmText: 'Reject All' })) return;
    setBulkLoading(true);
    try {
      await bulkVendorAction({ ids: [...selectedVendorIds], action: 'reject' });
      setSelectedVendorIds(new Set());
      await loadVendors();
      setStats((await getAdminStats()).data);
    } catch { alert('Bulk reject failed'); }
    finally { setBulkLoading(false); }
  };

  // Newsletter handlers
  const toggleLeadSelect = (id) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAllLeads = (filtered) => {
    setSelectedLeads(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(l => l.id)));
  };
  const handleSendNewsletter = async (sendAll = false) => {
    if (!newsletterSubject.trim() || !newsletterBody.trim()) { alert('Please enter a subject and body.'); return; }
    if (!sendAll && !selectedLeads.size) { alert('Select at least one recipient.'); return; }
    const emailableLeads = vendorLeads.filter(l => l.email);
    const count = sendAll ? emailableLeads.length : selectedLeads.size;
    if (count === 0) { alert('No vendors with email addresses to send to.'); return; }
    if (!await confirmDialog({ title: 'Send Newsletter', message: `Send newsletter to ${count} recipient(s) with email?`, confirmText: 'Send' })) return;
    setNewsletterSending(true);
    setNewsletterResult(null);
    try {
      const res = await sendNewsletterEmails({ ids: [...selectedLeads], subject: newsletterSubject, body: newsletterBody, send_all: sendAll, verified_only: verifiedOnly });
      setNewsletterResult(res.data);
    } catch (err) {
      setNewsletterResult({ error: err.response?.data?.error || 'Failed to send' });
    } finally { setNewsletterSending(false); }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div className="admin-page">
      <div className="admin-container">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-header-left">
            <Shield size={28} className="admin-icon" />
            <div>
              <h1>Admin Dashboard</h1>
              <p>Manage vendors, monitor activity, and oversee NepWed operations</p>
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/admin/verify-vendors')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            >
              <ShieldCheck size={16} /> Verify Vendors
            </button>
          </div>
        </div>

        {/* Stats Cards - Now clickable */}
        {stats && (
          <div className="admin-stats-grid">
            <div className="admin-stat-card clickable" onClick={() => handleStatClick('users')}>
              <div className="stat-icon users-icon"><Users size={22} /></div>
              <div className="stat-info">
                <span className="stat-number">{stats.totalUsers}</span>
                <span className="stat-label">Total Users</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
            <div className="admin-stat-card clickable" onClick={() => handleStatClick('vendors')}>
              <div className="stat-icon vendors-icon"><Store size={22} /></div>
              <div className="stat-info">
                <span className="stat-number">{stats.totalVendors}</span>
                <span className="stat-label">Total Vendors</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
            <div className="admin-stat-card highlight clickable" onClick={() => handleStatClick('pending')}>
              <div className="stat-icon pending-icon"><Clock size={22} /></div>
              <div className="stat-info">
                <span className="stat-number">{stats.pendingVendors}</span>
                <span className="stat-label">Pending Approval</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
            <div className="admin-stat-card clickable" onClick={() => handleStatClick('weddings')}>
              <div className="stat-icon weddings-icon"><Heart size={22} /></div>
              <div className="stat-info">
                <span className="stat-number">{stats.totalWeddings}</span>
                <span className="stat-label">Weddings</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
            <div className="admin-stat-card clickable" onClick={() => handleStatClick('reviews')}>
              <div className="stat-icon reviews-icon"><Star size={22} /></div>
              <div className="stat-info">
                <span className="stat-number">{stats.totalReviews}</span>
                <span className="stat-label">Reviews</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
            <div className="admin-stat-card clickable" onClick={() => handleStatClick('inquiries')}>
              <div className="stat-icon inquiries-icon"><MessageSquare size={22} /></div>
              <div className="stat-info">
                <span className="stat-number">{stats.totalInquiries}</span>
                <span className="stat-label">Inquiries</span>
              </div>
              <ChevronRight size={16} className="stat-arrow" />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="admin-tabs">
          <button className="admin-tab" onClick={() => navigate('/email-dashboard')}>
            <Mail size={16} /> Mailbox
            {mailboxUnread > 0 && (
              <span style={{ marginLeft: '6px', background: '#DC143C', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                {mailboxUnread}
              </span>
            )}
          </button>
          <button className="admin-tab" onClick={() => navigate('/admin/analytics')}>
            <Activity size={16} /> Analytics
          </button>
          <button className="admin-tab" onClick={() => navigate('/admin/releases')}>
            <Tag size={16} /> Releases
          </button>
          <button className="admin-tab" onClick={() => navigate('/admin/studio/contracts')}>
            <FileText size={16} /> Studio
          </button>
          <button className={`admin-tab ${activeTab === 'vendors' ? 'active' : ''}`} onClick={() => handleTabChange('vendors')}>
            <Store size={16} /> Vendors
          </button>
          <button className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => handleTabChange('users')}>
            <Users size={16} /> Users
          </button>
          <button className={`admin-tab ${activeTab === 'couples' ? 'active' : ''}`} onClick={() => handleTabChange('couples')}>
            <Heart size={16} /> Couples
          </button>
          <button className={`admin-tab ${activeTab === 'weddings' ? 'active' : ''}`} onClick={() => handleTabChange('weddings')}>
            <Calendar size={16} /> Weddings
          </button>
          <button className={`admin-tab ${activeTab === 'completed-weddings' ? 'active' : ''}`} onClick={() => handleTabChange('completed-weddings')}>
            <CheckCircle size={16} /> Completed Weddings
          </button>
          <button className={`admin-tab ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => handleTabChange('reviews')}>
            <Star size={16} /> Reviews
            {reviewsList.filter(r => r.rating < 3).length > 0 && (
              <span style={{ marginLeft: '6px', background: '#ef4444', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                {reviewsList.filter(r => r.rating < 3).length} ⚠
              </span>
            )}
          </button>
          <button className={`admin-tab ${activeTab === 'inquiries' ? 'active' : ''}`} onClick={() => handleTabChange('inquiries')}>
            <MessageSquare size={16} /> Inquiries
          </button>
          <button className={`admin-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => handleTabChange('activity')}>
            <Activity size={16} /> Activity
          </button>
          <button className={`admin-tab ${activeTab === 'google' ? 'active' : ''}`} onClick={() => { handleTabChange('google'); loadGoogleStatus(); }}>
            <Globe size={16} /> Google Sync
          </button>
          <button className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => handleTabChange('settings')}>
            <ShieldCheck size={16} /> Settings
          </button>
          <button className={`admin-tab ${activeTab === 'hero-images' ? 'active' : ''}`} onClick={() => handleTabChange('hero-images')}>
            <Image size={16} /> Hero Images
          </button>
          <button className={`admin-tab ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => handleTabChange('categories')}>
            <Store size={16} /> Categories
          </button>
          <button className={`admin-tab ${activeTab === 'family-requests' ? 'active' : ''}`} onClick={() => handleTabChange('family-requests')}>
            <Users size={16} /> Family Requests
          </button>
          <button className={`admin-tab ${activeTab === 'newsletter' ? 'active' : ''}`} onClick={() => handleTabChange('newsletter')}>
            <Mail size={16} /> Newsletter
          </button>
          <button className={`admin-tab ${activeTab === 'bidding-requests' ? 'active' : ''}`} onClick={() => handleTabChange('bidding-requests')}>
            <Gavel size={16} /> Bidding Requests
            {biddingRequests.filter(r => r.bidding_approved === 'pending').length > 0 && (
              <span style={{ marginLeft: '6px', background: '#f59e0b', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                {biddingRequests.filter(r => r.bidding_approved === 'pending').length}
              </span>
            )}
          </button>
          <button className={`admin-tab ${activeTab === 'invitations' ? 'active' : ''}`} onClick={() => handleTabChange('invitations')}>
            <Mail size={16} /> Invitations
            {adminInvitationStats.total > 0 && (
              <span style={{ marginLeft: '6px', background: '#be185d', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                {adminInvitationStats.total}
              </span>
            )}
          </button>
          <button className={`admin-tab ${activeTab === 'sms-logs' ? 'active' : ''}`} onClick={() => handleTabChange('sms-logs')}>
            <Phone size={16} /> SMS Logs
          </button>
        </div>

        {(loadError || tabError) && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>{loadError || tabError}</div>
        )}

        {/* VENDOR MANAGEMENT TAB */}
        {activeTab === 'vendors' && (
          <div className="admin-section">
            <div className="admin-filters">
              <div className="admin-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search vendors by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="admin-status-filters">
                <button className={`status-btn ${statusFilter === '' ? 'active' : ''}`} onClick={() => setStatusFilter('')}>All</button>
                <button className={`status-btn pending ${statusFilter === 'pending' ? 'active' : ''}`} onClick={() => setStatusFilter('pending')}>Pending</button>
                <button className={`status-btn approved ${statusFilter === 'approved' ? 'active' : ''}`} onClick={() => setStatusFilter('approved')}>Approved</button>
                <button className={`status-btn rejected ${statusFilter === 'rejected' ? 'active' : ''}`} onClick={() => setStatusFilter('rejected')}>Rejected</button>
                <button className={`status-btn suspended ${statusFilter === 'suspended' ? 'active' : ''}`} onClick={() => setStatusFilter('suspended')}>Suspended</button>
              </div>
            </div>

            <div className="admin-vendor-table">
              {selectedVendorIds.size > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#1D4ED8' }}>{selectedVendorIds.size} selected</span>
                  <button className="btn btn-outline" style={{ fontSize: '13px', padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#16a34a', borderColor: '#16a34a' }} onClick={handleBulkApprove} disabled={bulkLoading}>
                    <CheckCircle size={14} /> Approve
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: '13px', padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#dc2626', borderColor: '#dc2626' }} onClick={handleBulkReject} disabled={bulkLoading}>
                    <XCircle size={14} /> Reject
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: '13px', padding: '6px 14px' }} onClick={() => setSelectedVendorIds(new Set())}>
                    Clear
                  </button>
                </div>
              )}
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input type="checkbox" onChange={toggleSelectAll} checked={selectedVendorIds.size === vendors.length && vendors.length > 0} style={{ cursor: 'pointer' }} />
                    </th>
                    <th>Business Name</th>
                    <th>Category</th>
                    <th>Owner</th>
                    <th>City</th>
                    <th>Rating</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((v) => (
                    <tr key={v.id} className={`vendor-row ${v.approval_status}`} style={selectedVendorIds.has(v.id) ? { background: '#EFF6FF' } : {}}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={selectedVendorIds.has(v.id)} onChange={() => toggleVendorSelect(v.id)} style={{ cursor: 'pointer' }} />
                      </td>
                      <td>
                        <div className="vendor-name-cell">
                          <strong>{v.business_name}</strong>
                          <small>{v.email}</small>
                        </div>
                      </td>
                      <td>{v.category_name}</td>
                      <td>
                        <div className="vendor-name-cell">
                          <span>{v.owner_first_name} {v.owner_last_name}</span>
                          <small>{v.owner_email}</small>
                          {v.owner_email_verified === false && (
                            <small style={{ color: '#ef4444', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <XCircle size={11} /> Email not verified
                            </small>
                          )}
                        </div>
                      </td>
                      <td>{v.city}</td>
                      <td><Star size={14} className="star-filled" /> {v.rating}</td>
                      <td>
                        <span className={`approval-badge ${v.approval_status}`}>
                          {v.approval_status === 'pending' && <Clock size={12} />}
                          {v.approval_status === 'approved' && <CheckCircle size={12} />}
                          {v.approval_status === 'rejected' && <XCircle size={12} />}
                          {v.approval_status === 'suspended' && <Ban size={12} />}
                          {v.approval_status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {v.approval_status !== 'approved' && v.approval_status !== 'suspended' && (
                            <button className="action-btn approve" onClick={() => handleApprove(v.id)} disabled={actionLoading === v.id} title="Approve vendor">
                              <CheckCircle size={16} />
                            </button>
                          )}
                          {v.approval_status !== 'rejected' && v.approval_status !== 'suspended' && (
                            <button className="action-btn reject" onClick={() => handleReject(v.id)} disabled={actionLoading === v.id} title="Reject vendor">
                              <XCircle size={16} />
                            </button>
                          )}
                          {v.approval_status === 'approved' && (
                            <button className="action-btn suspend" onClick={() => handleSuspend(v.id)} disabled={actionLoading === v.id} title="Suspend vendor">
                              <Ban size={16} />
                            </button>
                          )}
                          {v.approval_status === 'suspended' && (
                            <button className="action-btn unsuspend" onClick={() => handleUnsuspend(v.id)} disabled={actionLoading === v.id} title="Unsuspend vendor">
                              <RotateCcw size={16} />
                            </button>
                          )}
                          <button className="action-btn view" title="View details" onClick={() => handleViewVendor(v.id)}>
                            <Eye size={16} />
                          </button>
                          <button className="action-btn reject" title="Delete vendor permanently" onClick={async () => {
                            if (!await confirmDialog({ title: 'Delete Vendor', message: `Permanently delete vendor "${v.business_name}"? This cannot be undone.`, danger: true, confirmText: 'Delete' })) return;
                            try {
                              await adminDeleteVendor(v.id);
                              await loadVendors();
                              setStats((await getAdminStats()).data);
                            } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                          }}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {vendors.length === 0 && (
                    <tr><td colSpan="8" className="no-data">No vendors found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div className="admin-section">
            <div className="admin-filters" style={{ marginBottom: '16px' }}>
              <div className="admin-search">
                <Search size={16} />
                <input type="text" placeholder="Search users by name, email, or role..." value={tabSearch} onChange={(e) => setTabSearch(e.target.value)} />
              </div>
            </div>
            {/* Role filter tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {['all', 'couple', 'vendor', 'helper', 'admin'].map(r => {
                const count = r === 'all' ? usersList.length : usersList.filter(u => u.role === r).length;
                return (
                  <button key={r} onClick={() => setUserRoleFilter(r)}
                    style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: userRoleFilter === r ? '2px solid #DC143C' : '2px solid #e5e7eb', background: userRoleFilter === r ? '#fef2f2' : '#fff', color: userRoleFilter === r ? '#DC143C' : '#374151', transition: 'all 0.2s' }}>
                    {r.charAt(0).toUpperCase() + r.slice(1)} ({count})
                  </button>
                );
              })}
            </div>
            {tabLoading ? (
              <div className="tab-loading"><div className="spinner" /></div>
            ) : (
              <div className="admin-vendor-table">
                <table>
                  <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Joined</th><th>Actions</th></tr></thead>
                  <tbody>
                    {usersList.filter(u => (userRoleFilter === 'all' || u.role === userRoleFilter) && matchesSearch(u, ['first_name', 'last_name', 'email', 'phone', 'role'])).map((u) => (
                      <tr key={u.id}>
                        <td><strong>{u.first_name} {u.last_name}</strong></td>
                        <td>{u.email}</td>
                        <td>{u.phone || '—'}</td>
                        <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                        <td>{formatDate(u.created_at)}</td>
                        <td>
                          {u.role !== 'admin' && (
                            <button className="action-btn reject" title="Delete user permanently" onClick={async () => {
                              if (!await confirmDialog({ title: 'Delete User', message: `Permanently delete user "${u.first_name} ${u.last_name}" (${u.role})? This cannot be undone.`, danger: true, confirmText: 'Delete' })) return;
                              try {
                                await adminDeleteUser(u.id);
                                const res = await getAdminUsers({});
                                setUsersList(res.data);
                              } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {usersList.length === 0 && <tr><td colSpan="6" className="no-data">No users found</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* COUPLES TAB */}
        {activeTab === 'couples' && (
          <div className="admin-section">
            <div className="admin-filters" style={{ marginBottom: '16px' }}>
              <div className="admin-search">
                <Search size={16} />
                <input type="text" placeholder="Search couples by name, email, or partners..." value={tabSearch} onChange={(e) => setTabSearch(e.target.value)} />
              </div>
            </div>
            {tabLoading ? (
              <div className="tab-loading"><div className="spinner" /></div>
            ) : (
              <div className="admin-vendor-table">
                <table>
                  <thead><tr><th>Name</th><th>Email</th><th>Wedding</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {couplesList.filter(c => matchesSearch(c, ['first_name', 'last_name', 'email', 'partner1_name', 'partner2_name'])).map((c) => (
                      <tr key={`${c.id}-${c.wedding_id || 'no'}`}>
                        <td><strong>{c.first_name} {c.last_name}</strong></td>
                        <td>{c.email}</td>
                        <td>{c.partner1_name && c.partner2_name ? `${c.partner1_name} & ${c.partner2_name}` : '—'}</td>
                        <td>{c.wedding_date ? formatDate(c.wedding_date) : '—'}</td>
                        <td>
                          {c.wedding_status
                            ? <span className={`approval-badge ${c.wedding_status}`}>{c.wedding_status}</span>
                            : <span className="approval-badge pending">No wedding</span>
                          }
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="action-btn view" title="View couple details" onClick={() => handleViewCouple(c.id)}>
                            <Eye size={16} />
                          </button>
                          <button className="action-btn reject" title="Delete couple permanently" onClick={async () => {
                            if (!await confirmDialog({ title: 'Delete Couple', message: `Permanently delete couple "${c.first_name} ${c.last_name}" and all their wedding data? This cannot be undone.`, danger: true, confirmText: 'Delete' })) return;
                            try {
                              await adminDeleteCouple(c.id);
                              const res = await getAdminCouples();
                              setCouplesList(res.data);
                            } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                          }}>
                            <Trash2 size={16} />
                          </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {couplesList.length === 0 && <tr><td colSpan="6" className="no-data">No couples found</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* WEDDINGS TAB */}
        {activeTab === 'weddings' && (
          <div className="admin-section">
            <div className="admin-filters" style={{ marginBottom: '16px' }}>
              <div className="admin-search">
                <Search size={16} />
                <input type="text" placeholder="Search weddings by couple, partners, or venue..." value={tabSearch} onChange={(e) => setTabSearch(e.target.value)} />
              </div>
            </div>
            {tabLoading ? (
              <div className="tab-loading"><div className="spinner" /></div>
            ) : (
              <div className="admin-vendor-table">
                <table>
                  <thead><tr><th>Couple</th><th>Partners</th><th>Date</th><th>Venue</th><th>Guests</th><th>Budget</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {weddingsList.filter(w => matchesSearch(w, ['first_name', 'last_name', 'email', 'partner1_name', 'partner2_name', 'venue_location', 'city'])).map((w) => (
                      <tr key={w.id} style={w.is_deleted ? { opacity: 0.6, background: '#fef2f2' } : {}}>
                        <td>
                          <div className="vendor-name-cell">
                            <strong>{w.first_name} {w.last_name}</strong>
                            <small>{w.email}</small>
                          </div>
                        </td>
                        <td>{w.partner1_name} & {w.partner2_name}</td>
                        <td>{formatDate(w.wedding_date)}</td>
                        <td>{w.venue_location || '—'}</td>
                        <td>{w.guest_count}</td>
                        <td>{formatNPR(w.budget_npr)}</td>
                        <td>
                          {w.is_deleted ? (
                            <span className="approval-badge rejected" style={{ background: '#fecaca', color: '#991b1b' }}>Deleted</span>
                          ) : w.status === 'suspended' ? (
                            <span className="approval-badge" style={{ background: '#fef3c7', color: '#92400e' }}>Suspended</span>
                          ) : (
                            <span className={`approval-badge ${w.status}`}>{w.status}</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {w.is_deleted ? (
                            <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '12px' }}
                              onClick={async () => {
                                try {
                                  await restoreWedding(w.id);
                                  const res = await getAdminWeddings();
                                  setWeddingsList(res.data);
                                } catch (err) {
                                  alert('Failed to restore: ' + (err.response?.data?.error || err.message));
                                }
                              }}>
                              <RotateCcw size={12} /> Restore
                            </button>
                          ) : (
                            <>
                              {w.status === 'suspended' ? (
                                <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '12px', color: '#16a34a', borderColor: '#16a34a' }}
                                  onClick={async () => {
                                    try {
                                      await unsuspendWedding(w.id);
                                      const res = await getAdminWeddings();
                                      setWeddingsList(res.data);
                                    } catch (err) {
                                      alert('Failed: ' + (err.response?.data?.error || err.message));
                                    }
                                  }}>
                                  <RotateCcw size={12} /> Unsuspend
                                </button>
                              ) : (
                                <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '12px', color: '#f59e0b', borderColor: '#f59e0b' }}
                                  onClick={async () => {
                                    const reason = await promptDialog({ title: 'Suspend Wedding', message: 'Reason for suspension (optional):', placeholder: 'Enter reason...' });
                                    if (reason === null) return;
                                    try {
                                      await suspendWedding(w.id, { reason });
                                      const res = await getAdminWeddings();
                                      setWeddingsList(res.data);
                                    } catch (err) {
                                      alert('Failed: ' + (err.response?.data?.error || err.message));
                                    }
                                  }}>
                                  <Ban size={12} /> Suspend
                                </button>
                              )}
                              <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '12px', color: '#ef4444', borderColor: '#ef4444' }}
                                onClick={async () => {
                                  if (!await confirmDialog({ title: 'Delete Wedding', message: `Delete wedding of ${w.partner1_name} & ${w.partner2_name}? This is a soft delete and can be restored.`, danger: true, confirmText: 'Delete' })) return;
                                  try {
                                    await adminDeleteWedding(w.id);
                                    const res = await getAdminWeddings();
                                    setWeddingsList(res.data);
                                  } catch (err) {
                                    alert('Failed: ' + (err.response?.data?.error || err.message));
                                  }
                                }}>
                                <Trash2 size={12} /> Delete
                              </button>
                            </>
                          )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {weddingsList.length === 0 && <tr><td colSpan="8" className="no-data">No weddings found</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* COMPLETED WEDDINGS TAB */}
        {activeTab === 'completed-weddings' && (
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ margin: 0 }}>Completed Weddings</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                  Weddings with a past event date. Mark as complete to enable couple reviews and send the review request email.
                </p>
              </div>
              <button className="btn btn-outline" onClick={() => { setCompletedLoading(true); getAdminCompletedWeddings().then(r => setCompletedWeddings(r.data)).finally(() => setCompletedLoading(false)); }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <RefreshCw size={14} /> Refresh
              </button>
            </div>

            {completedLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner"></div></div>
            ) : completedWeddings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                <CheckCircle size={40} color="#d1d5db" />
                <p style={{ marginTop: '12px' }}>No past-date weddings found.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Couple</th>
                      <th>Email</th>
                      <th>Wedding Date</th>
                      <th>Venue / City</th>
                      <th>Booked Vendors</th>
                      <th>Reviews</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedWeddings.map(w => (
                      <tr key={w.id} style={{ background: w.review_enabled ? '#f0fdf4' : undefined }}>
                        <td>
                          <strong>{w.partner1_name}{w.partner2_name ? ` & ${w.partner2_name}` : ''}</strong>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{w.first_name} {w.last_name}</div>
                        </td>
                        <td style={{ fontSize: '13px' }}>{w.email}</td>
                        <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {w.wedding_date ? new Date(w.wedding_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td style={{ fontSize: '13px' }}>{[w.venue_location, w.city].filter(Boolean).join(', ') || '—'}</td>
                        <td style={{ textAlign: 'center', fontSize: '13px' }}>{w.booked_vendor_count}</td>
                        <td style={{ textAlign: 'center', fontSize: '13px' }}>
                          {w.review_count > 0
                            ? <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ {w.review_count}</span>
                            : <span style={{ color: '#9ca3af' }}>0</span>}
                        </td>
                        <td>
                          {w.review_enabled
                            ? <span style={{ fontSize: '12px', fontWeight: 600, color: '#22c55e', background: '#f0fdf4', padding: '3px 10px', borderRadius: '20px' }}>Review Enabled</span>
                            : <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: '20px' }}>Pending</span>}
                        </td>
                        <td>
                          {!w.review_enabled ? (
                            <button
                              className="btn btn-primary"
                              style={{ fontSize: '12px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '5px' }}
                              disabled={actionLoading === `complete-${w.id}`}
                              onClick={async () => {
                                if (!await confirmDialog({ title: 'Mark Wedding Complete', message: `Mark this wedding complete and email the review request to ${w.email}?`, confirmText: 'Mark Complete & Email' })) return;
                                setActionLoading(`complete-${w.id}`);
                                try {
                                  const res = await adminMarkWeddingComplete(w.id);
                                  alert(`✅ Wedding marked complete!\n${res.data.emailSent ? 'Review request email sent to couple.' : 'Email could not be sent (check SMTP settings).'}`);
                                  const updated = await getAdminCompletedWeddings();
                                  setCompletedWeddings(updated.data);
                                } catch (err) {
                                  alert('Error: ' + (err.response?.data?.error || err.message));
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                            >
                              <CheckCircle size={13} />
                              {actionLoading === `complete-${w.id}` ? 'Sending…' : 'Mark Complete & Email'}
                            </button>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                              Done {w.completion_verified_at ? new Date(w.completion_verified_at).toLocaleDateString() : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <div className="admin-section">
            <div className="admin-filters" style={{ marginBottom: '16px' }}>
              <div className="admin-search">
                <Search size={16} />
                <input type="text" placeholder="Search reviews by reviewer, vendor, or title..." value={tabSearch} onChange={(e) => setTabSearch(e.target.value)} />
              </div>
            </div>
            {tabLoading ? (
              <div className="tab-loading"><div className="spinner" /></div>
            ) : (
              <>
                {/* Low-Star Reviews Alert Section */}
                {reviewsList.filter(r => r.rating < 3).length > 0 && (
                  <div style={{ marginBottom: '24px', border: '1.5px solid #fca5a5', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ background: '#fef2f2', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #fca5a5' }}>
                      <span style={{ fontSize: '18px' }}>⚠️</span>
                      <strong style={{ color: '#b91c1c', fontSize: '15px' }}>Low Ratings (under 3 stars)</strong>
                      <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 700 }}>
                        {reviewsList.filter(r => r.rating < 3).length} reviews
                      </span>
                    </div>
                    <div className="admin-vendor-table" style={{ marginBottom: 0 }}>
                      <table>
                        <thead><tr><th>Reviewer</th><th>Vendor</th><th>Rating</th><th>Title</th><th>Date</th></tr></thead>
                        <tbody>
                          {reviewsList.filter(r => r.rating < 3).map((r) => (
                            <tr key={`low-${r.id}`} style={{ background: '#fff5f5' }}>
                              <td>
                                <div className="vendor-name-cell">
                                  <strong>{r.first_name} {r.last_name}</strong>
                                  <small>{r.reviewer_email}</small>
                                </div>
                              </td>
                              <td>{r.vendor_name}</td>
                              <td style={{ color: '#ef4444', fontWeight: 700 }}><Star size={14} className="star-filled" /> {r.rating}/5</td>
                              <td>{r.title || '—'}</td>
                              <td>{formatDate(r.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* All Reviews Table */}
                <div className="admin-vendor-table">
                  <table>
                    <thead><tr><th>Reviewer</th><th>Vendor</th><th>Rating</th><th>Title</th><th>Date</th></tr></thead>
                    <tbody>
                      {reviewsList.filter(r => matchesSearch(r, ['first_name', 'last_name', 'reviewer_email', 'vendor_name', 'title'])).map((r) => (
                        <tr key={r.id}>
                          <td>
                            <div className="vendor-name-cell">
                              <strong>{r.first_name} {r.last_name}</strong>
                              <small>{r.reviewer_email}</small>
                            </div>
                          </td>
                          <td>{r.vendor_name}</td>
                          <td><Star size={14} className="star-filled" /> {r.rating}/5</td>
                          <td>{r.title || '—'}</td>
                          <td>{formatDate(r.created_at)}</td>
                        </tr>
                      ))}
                      {reviewsList.length === 0 && <tr><td colSpan="5" className="no-data">No reviews found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* INQUIRIES TAB */}
        {activeTab === 'inquiries' && (
          <div className="admin-section">
            <div className="admin-filters" style={{ marginBottom: '16px' }}>
              <div className="admin-search">
                <Search size={16} />
                <input type="text" placeholder="Search inquiries by sender, vendor, or message..." value={tabSearch} onChange={(e) => setTabSearch(e.target.value)} />
              </div>
            </div>
            {tabLoading ? (
              <div className="tab-loading"><div className="spinner" /></div>
            ) : (
              <div className="admin-vendor-table">
                <table>
                  <thead><tr><th>From</th><th>To Vendor</th><th>Message</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>
                    {inquiriesList.filter(i => matchesSearch(i, ['first_name', 'last_name', 'inquirer_email', 'vendor_name', 'message', 'status'])).map((i) => (
                      <React.Fragment key={i.id}>
                      <tr ref={i.id === highlightInqId ? highlightRowRef : undefined}
                        className={`${i.message && i.message.startsWith('[SUSPENSION APPEAL]') ? 'suspension-appeal-row' : ''}`}
                        style={{ cursor: 'pointer', ...(i.id === highlightInqId ? { background: '#fef2f2', boxShadow: 'inset 3px 0 0 #DC143C' } : {}), ...(expandedInqId === i.id ? { background: '#eff6ff' } : {}) }}
                        onClick={async () => {
                          if (expandedInqId === i.id) { setExpandedInqId(null); return; }
                          setExpandedInqId(i.id);
                          setInqMsgLoading(true);
                          try {
                            const res = await getInquiryMessages(i.id);
                            setInqMessages(res.data || []);
                            if (!i.admin_read) {
                              await markAdminInquiryRead(i.id);
                              setInquiriesList(prev => prev.map(x => x.id === i.id ? { ...x, admin_read: true } : x));
                            }
                          } catch { setInqMessages([]); }
                          setInqMsgLoading(false);
                        }}>
                        <td>
                          <div className="vendor-name-cell">
                            <strong style={!i.admin_read ? { fontWeight: 800 } : {}}>{i.first_name} {i.last_name}</strong>
                            <small>{i.inquirer_email}</small>
                          </div>
                        </td>
                        <td>{i.vendor_name}</td>
                        <td className="message-cell">
                          {i.message && i.message.startsWith('[SUSPENSION APPEAL]') && <AlertTriangle size={14} className="suspension-icon" />}
                          {i.message ? (i.message.length > 80 ? i.message.substring(0, 80) + '...' : i.message) : '—'}
                        </td>
                        <td><span className={`inquiry-badge ${i.status}`}>{i.status}</span></td>
                        <td>{formatDate(i.created_at)}</td>
                      </tr>
                      {expandedInqId === i.id && (
                        <tr><td colSpan="5" style={{ padding: 0, background: '#f9fafb' }}>
                          <div style={{ padding: '16px 20px', maxHeight: 400, overflowY: 'auto' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Conversation Thread</div>
                            {inqMsgLoading ? <div className="spinner" style={{ margin: '12px auto' }} /> : inqMessages.length === 0 ? (
                              <div style={{ color: '#9ca3af', fontSize: 13 }}>No messages in this thread</div>
                            ) : inqMessages.map((m, idx) => (
                              <div key={m.id || idx} style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: m.sender_type === 'couple' ? '#eff6ff' : m.sender_type === 'vendor' ? '#f0fdf4' : '#fef3c7', borderLeft: `3px solid ${m.sender_type === 'couple' ? '#3b82f6' : m.sender_type === 'vendor' ? '#22c55e' : '#f59e0b'}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: m.sender_type === 'couple' ? '#1d4ed8' : m.sender_type === 'vendor' ? '#15803d' : '#b45309', textTransform: 'capitalize' }}>{m.sender_type}</span>
                                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(m.created_at)}</span>
                                </div>
                                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{m.message}</div>
                                {m.attachment_url && <a href={mediaUrl(m.attachment_url)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', marginTop: 4, display: 'inline-block' }}>📎 {m.attachment_name || 'Attachment'}</a>}
                              </div>
                            ))}
                          </div>
                        </td></tr>
                      )}
                      </React.Fragment>
                    ))}
                    {inquiriesList.length === 0 && <tr><td colSpan="5" className="no-data">No inquiries found</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ACTIVITY MONITOR TAB */}
        {activeTab === 'activity' && activity && (
          <div className="admin-section">
            <div className="activity-grid">
              <div className="activity-card">
                <h3><Store size={18} /> Recent Vendors</h3>
                <div className="activity-list">
                  {activity.recentVendors.map((v) => (
                    <div key={v.id} className="activity-item">
                      <div className="activity-info">
                        <strong>{v.business_name}</strong>
                        <small>{v.owner_email}</small>
                      </div>
                      <span className={`approval-badge small ${v.approval_status}`}>{v.approval_status}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="activity-card">
                <h3><Users size={18} /> Recent Signups</h3>
                <div className="activity-list">
                  {activity.recentUsers.map((u) => (
                    <div key={u.id} className="activity-item">
                      <div className="activity-info">
                        <strong>{u.first_name} {u.last_name}</strong>
                        <small>{u.email}</small>
                        {u.role === 'helper' && u.helper_wedding_id && (
                          <small style={{ color: '#be185d', cursor: 'pointer', display: 'block', marginTop: '2px' }}
                            onClick={() => handleTabChange('weddings')}>
                            🔗 {u.helper_wedding_partner1} & {u.helper_wedding_partner2}'s wedding
                          </small>
                        )}
                      </div>
                      <span className={`role-badge ${u.role}`}>{u.role}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="activity-card">
                <h3><Star size={18} /> Recent Reviews</h3>
                <div className="activity-list">
                  {activity.recentReviews.map((r) => (
                    <div key={r.id} className="activity-item">
                      <div className="activity-info">
                        <strong>{r.first_name} {r.last_name}</strong>
                        <small>Reviewed {r.vendor_name}</small>
                      </div>
                      <span className="review-rating"><Star size={12} className="star-filled" /> {r.rating}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="activity-card">
                <h3><MessageSquare size={18} /> Recent Inquiries</h3>
                <div className="activity-list">
                  {activity.recentInquiries.map((i) => (
                    <div key={i.id} className="activity-item">
                      <div className="activity-info">
                        <strong>{i.first_name} {i.last_name}</strong>
                        <small>to {i.vendor_name}</small>
                      </div>
                      <span className={`inquiry-badge ${i.status}`}>{i.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* GOOGLE SYNC TAB */}
        {activeTab === 'google' && (
          <div className="admin-section">
            <div className="section-header">
              <h2><Globe size={20} /> Google Places Vendor Sync</h2>
              <p style={{color: 'var(--text-secondary)', margin: '4px 0 16px'}}>Pull wedding-related vendors from all major Nepal cities via Google Places API</p>
            </div>

            {syncResult && (
              <div className={`alert ${syncResult.error ? 'alert-error' : 'alert-success'}`} style={{marginBottom: 16}}>
                {syncResult.error ? syncResult.error : `Sync complete: ${syncResult.added || 0} added, ${syncResult.updated || 0} updated, ${syncResult.skipped || 0} skipped${syncResult.errors?.length ? `, ${syncResult.errors.length} failed` : ''}`}
              </div>
            )}

            <div className="google-sync-actions" style={{display: 'flex', gap: 12, marginBottom: 24}}>
              <button
                className="btn btn-primary"
                disabled={syncLoading}
                onClick={handleSyncAll}
              >
                {syncLoading ? <><RefreshCw size={16} className="spin" /> Syncing...</> : <><Globe size={16} /> Sync All Categories</>}
              </button>
              <button
                className="btn btn-outline"
                disabled={syncLoading}
                onClick={loadGoogleStatus}
              >
                <RefreshCw size={16} /> Refresh Status
              </button>
            </div>

            {syncStatus && (
              <div className="google-sync-status">
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">{syncStatus.total_google_vendors || 0}</div>
                    <div className="stat-label">Google Vendors</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{syncStatus.total_manual_vendors || 0}</div>
                    <div className="stat-label">Manual Vendors</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{syncStatus.categories_with_google?.length || 0}</div>
                    <div className="stat-label">Categories Covered</div>
                  </div>
                </div>
                {syncStatus.categories_with_google?.length > 0 && (
                  <div style={{marginTop: 16}}>
                    <h3 style={{marginBottom: 8}}>Categories with Google Vendors</h3>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                      {syncStatus.categories_with_google.map((cat, i) => (
                        <span key={i} className="permission-tag" style={{background: 'var(--primary-light)', color: 'var(--primary)'}}>
                          {cat.name}: {cat.count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* EMAIL TEST SECTION (inside Google tab) */}
        {activeTab === 'google' && (
          <div className="admin-section" style={{ marginTop: '24px' }}>
            <div className="section-header">
              <h2><Mail size={20} /> Email SMTP Test</h2>
              <p style={{color: 'var(--text-secondary)', margin: '4px 0 16px'}}>Send a test email to verify SMTP configuration (AWS SES / Gmail)</p>
            </div>
            {emailTestResult && (
              <div className={`alert ${emailTestResult.error ? 'alert-error' : 'alert-success'}`} style={{marginBottom: 16}}>
                {emailTestResult.error || emailTestResult.message}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="email"
                placeholder="recipient@example.com"
                value={testEmailAddr}
                onChange={(e) => setTestEmailAddr(e.target.value)}
                style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', flex: 1, maxWidth: '320px' }}
              />
              <button
                className="btn btn-primary"
                disabled={emailTesting || !testEmailAddr}
                onClick={async () => {
                  setEmailTesting(true);
                  setEmailTestResult(null);
                  try {
                    const res = await testAdminEmail(testEmailAddr);
                    setEmailTestResult({ message: `✅ Email sent to ${testEmailAddr} via ${res.data.smtp_host}` });
                  } catch (err) {
                    setEmailTestResult({ error: err.response?.data?.error || 'Failed to send test email' });
                  } finally {
                    setEmailTesting(false);
                  }
                }}
              >
                <Mail size={16} /> {emailTesting ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </div>
        )}

      {/* VENDOR DETAIL MODAL */}
      {(vendorDetail || vendorDetailLoading) && (
        <div className="admin-modal-overlay" onClick={() => !vendorDetailLoading && setVendorDetail(null)}>
          <div className="admin-modal vendor-detail-modal" onClick={(e) => e.stopPropagation()}>
            {vendorDetailLoading ? (
              <div className="modal-loading"><div className="spinner" /></div>
            ) : vendorDetail && (
              <>
                <div className="modal-header">
                  <h2><Store size={20} /> {vendorDetail.business_name}</h2>
                  <button className="modal-close" onClick={() => setVendorDetail(null)}><X size={20} /></button>
                </div>
                <div className="modal-body">
                  <div className={`vendor-detail-status ${vendorDetail.approval_status}`}>
                    {vendorDetail.approval_status === 'suspended' && <Ban size={16} />}
                    {vendorDetail.approval_status === 'pending' && <Clock size={16} />}
                    {vendorDetail.approval_status === 'approved' && <CheckCircle size={16} />}
                    {vendorDetail.approval_status === 'rejected' && <XCircle size={16} />}
                    <span>Status: {vendorDetail.approval_status.toUpperCase()}</span>
                  </div>

                  {/* Bidding Access Status */}
                  {vendorDetail.bidding_approved && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', marginBottom: '16px',
                      background: vendorDetail.bidding_approved === 'approved' ? '#f0fdf4' : vendorDetail.bidding_approved === 'pending' ? '#fffbeb' : '#fef2f2',
                      border: `1px solid ${vendorDetail.bidding_approved === 'approved' ? '#bbf7d0' : vendorDetail.bidding_approved === 'pending' ? '#fde68a' : '#fecaca'}`,
                    }}>
                      <Gavel size={16} style={{ color: vendorDetail.bidding_approved === 'approved' ? '#16a34a' : vendorDetail.bidding_approved === 'pending' ? '#d97706' : '#dc2626' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: vendorDetail.bidding_approved === 'approved' ? '#16a34a' : vendorDetail.bidding_approved === 'pending' ? '#92400e' : '#dc2626' }}>
                        Bidding: {vendorDetail.bidding_approved.toUpperCase()}
                      </span>
                      {vendorDetail.bidding_approved === 'pending' && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                          <button className="btn btn-outline" style={{ fontSize: '12px', padding: '4px 10px', color: '#16a34a', borderColor: '#16a34a' }}
                            onClick={async () => {
                              try {
                                await approveBiddingRequest(vendorDetail.id);
                                const detail = await getAdminVendorDetail(vendorDetail.id);
                                setVendorDetail(detail.data);
                              } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}>
                            <CheckCircle size={12} /> Approve
                          </button>
                          <button className="btn btn-outline" style={{ fontSize: '12px', padding: '4px 10px', color: '#dc2626', borderColor: '#dc2626' }}
                            onClick={async () => {
                              try {
                                await denyBiddingRequest(vendorDetail.id);
                                const detail = await getAdminVendorDetail(vendorDetail.id);
                                setVendorDetail(detail.data);
                              } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}>
                            <XCircle size={12} /> Deny
                          </button>
                        </div>
                      )}
                      {vendorDetail.bidding_approved === 'approved' && (
                        <button className="btn btn-outline" style={{ marginLeft: 'auto', fontSize: '12px', padding: '4px 10px', color: '#dc2626', borderColor: '#dc2626' }}
                          onClick={async () => {
                            if (!await confirmDialog({ title: 'Revoke Bidding', message: 'Revoke bidding access?', danger: true, confirmText: 'Revoke' })) return;
                            try {
                              await revokeBiddingAccess(vendorDetail.id);
                              const detail = await getAdminVendorDetail(vendorDetail.id);
                              setVendorDetail(detail.data);
                            } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                          }}>
                          <Ban size={12} /> Revoke
                        </button>
                      )}
                    </div>
                  )}

                  <div className="vendor-detail-grid">
                    <div className="detail-section">
                      <h3>Business Information</h3>
                      <div className="detail-row"><MapPin size={14} /> <span>{vendorDetail.address || vendorDetail.city}, {vendorDetail.district || ''}</span></div>
                      <div className="detail-row"><Phone size={14} /> <span>{vendorDetail.phone || 'N/A'}</span></div>
                      <div className="detail-row"><Mail size={14} /> <span>{vendorDetail.email || 'N/A'}</span></div>
                      <div className="detail-row"><Store size={14} /> <span>Category: {vendorDetail.category_name}</span></div>
                      <div className="detail-row"><Star size={14} /> <span>Rating: {vendorDetail.rating}/5 ({vendorDetail.review_count} reviews)</span></div>
                      <div className="detail-row"><DollarSign size={14} /> <span>Price: {vendorDetail.price_range} ({formatNPR(vendorDetail.min_price_npr)} - {formatNPR(vendorDetail.max_price_npr)})</span></div>
                      {vendorDetail.description && <p className="vendor-detail-desc">{vendorDetail.description}</p>}
                    </div>
                    <div className="detail-section">
                      <h3>Owner Information</h3>
                      <div className="detail-row"><Users size={14} /> <span>{vendorDetail.owner_first_name} {vendorDetail.owner_last_name}</span></div>
                      <div className="detail-row"><Mail size={14} /> <span>{vendorDetail.owner_email}</span></div>
                      <div className="detail-row"><Phone size={14} /> <span>{vendorDetail.owner_phone || 'N/A'}</span></div>
                      <div className="detail-row"><Calendar size={14} /> <span>Joined: {formatDate(vendorDetail.owner_joined)}</span></div>
                      {vendorDetail.owner_email_verified === false && (
                        <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#dc2626', fontSize: '13px', fontWeight: 600 }}>
                          <XCircle size={14} /> Email not verified — vendor cannot log in
                        </div>
                      )}
                      {vendorDetail.owner_email_verified === true && (
                        <div style={{ marginTop: '8px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#16a34a', fontSize: '13px', fontWeight: 600 }}>
                          <CheckCircle size={14} /> Email verified
                        </div>
                      )}

                      {/* Temp Password Section */}
                      <div style={{ marginTop: '16px', padding: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <Key size={14} style={{ color: '#d97706' }} />
                          <strong style={{ fontSize: '13px', color: '#92400e' }}>Login Credentials</strong>
                        </div>
                        {vendorDetail.temp_password ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <code style={{ background: '#fef3c7', padding: '4px 10px', borderRadius: '4px', fontSize: '14px', fontWeight: 700, color: '#be185d', letterSpacing: '0.5px' }}>
                              {vendorDetail.temp_password}
                            </code>
                            <button
                              className="btn btn-outline"
                              style={{ padding: '4px 8px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              onClick={() => { navigator.clipboard.writeText(vendorDetail.temp_password); }}
                              title="Copy password"
                            >
                              <Copy size={12} /> Copy
                            </button>
                            <button
                              className="btn btn-outline"
                              style={{ padding: '4px 8px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#d97706', borderColor: '#d97706' }}
                              onClick={async () => {
                                if (!await confirmDialog({ title: 'Reset Password', message: 'Reset this vendor\'s password? A new temp password will be generated and emailed.', confirmText: 'Reset' })) return;
                                try {
                                  const res = await resetVendorPassword(vendorDetail.id);
                                  alert(`New password: ${res.data.tempPassword}\n\n${res.data.emailSent === false ? 'The email to the vendor could not be sent. Pass this password on yourself.' : 'Also emailed to the vendor.'}`);
                                  // Refresh vendor detail
                                  const detail = await getAdminVendorDetail(vendorDetail.id);
                                  setVendorDetail(detail.data);
                                } catch (err) {
                                  alert('Failed: ' + (err.response?.data?.error || err.message));
                                }
                              }}
                              title="Generate new password"
                            >
                              <RefreshCcw size={12} /> Reset
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {/* Also the state once the vendor sets their own password — that clears it */}
                            <span style={{ fontSize: '13px', color: '#6b7280' }}>No temporary password in use</span>
                            {vendorDetail.approval_status === 'approved' && (
                              <button
                                className="btn btn-outline"
                                style={{ padding: '4px 8px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#d97706', borderColor: '#d97706' }}
                                onClick={async () => {
                                  // Replaces the password the vendor set themselves
                                  if (!await confirmDialog({ title: 'Generate Password', message: 'Replace this vendor\'s current password with a new temporary one? Their old password stops working; the new one is emailed to them.', confirmText: 'Generate' })) return;
                                  try {
                                    const res = await resetVendorPassword(vendorDetail.id);
                                    alert(`Password: ${res.data.tempPassword}\n\n${res.data.emailSent === false ? 'The email to the vendor could not be sent. Pass this password on yourself.' : 'Also emailed to the vendor.'}`);
                                    const detail = await getAdminVendorDetail(vendorDetail.id);
                                    setVendorDetail(detail.data);
                                  } catch (err) {
                                    alert('Failed: ' + (err.response?.data?.error || err.message));
                                  }
                                }}
                              >
                                <Key size={12} /> Generate Password
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3><Star size={16} /> Reviews ({vendorDetail.reviews.length})</h3>
                    {vendorDetail.reviews.length === 0 ? <p className="no-data-text">No reviews yet</p> : (
                      <div className="detail-list">
                        {vendorDetail.reviews.map((r) => (
                          <div key={r.id} className="detail-list-item">
                            <div className="detail-list-header">
                              <strong>{r.first_name} {r.last_name}</strong>
                              <span className="review-rating"><Star size={12} className="star-filled" /> {r.rating}/5</span>
                            </div>
                            <p className="detail-list-title">{r.title}</p>
                            {r.body && <p className="detail-list-body">{r.body}</p>}
                            <small>{formatDate(r.created_at)}</small>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <h3><MessageSquare size={16} /> Inquiries ({vendorDetail.inquiries.length})</h3>
                    {vendorDetail.inquiries.length === 0 ? <p className="no-data-text">No inquiries yet</p> : (
                      <div className="detail-list">
                        {vendorDetail.inquiries.map((i) => (
                          <div key={i.id} className={`detail-list-item ${i.message && i.message.startsWith('[SUSPENSION APPEAL]') ? 'suspension-appeal' : ''}`}>
                            <div className="detail-list-header">
                              <strong>{i.first_name} {i.last_name}</strong>
                              <span className={`inquiry-badge ${i.status}`}>{i.status}</span>
                            </div>
                            <p className="detail-list-body">{i.message}</p>
                            <small>{formatDate(i.created_at)}</small>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="modal-actions">
                    {vendorDetail.approval_status === 'suspended' && (
                      <button className="btn btn-approve" onClick={() => { handleUnsuspend(vendorDetail.id); setVendorDetail(null); }}>
                        <RotateCcw size={16} /> Unsuspend
                      </button>
                    )}
                    {vendorDetail.approval_status === 'approved' && (
                      <button className="btn btn-suspend" onClick={() => { handleSuspend(vendorDetail.id); setVendorDetail(null); }}>
                        <Ban size={16} /> Suspend
                      </button>
                    )}
                    {vendorDetail.approval_status === 'pending' && (
                      <>
                        <button className="btn btn-approve" onClick={() => { handleApprove(vendorDetail.id); setVendorDetail(null); }}>
                          <CheckCircle size={16} /> Approve
                        </button>
                        <button className="btn btn-reject" onClick={() => { handleReject(vendorDetail.id); setVendorDetail(null); }}>
                          <XCircle size={16} /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* COUPLE DETAIL MODAL */}
      {(coupleDetail || coupleDetailLoading) && (
        <div className="admin-modal-overlay" onClick={() => !coupleDetailLoading && setCoupleDetail(null)}>
          <div className="admin-modal couple-detail-modal" onClick={(e) => e.stopPropagation()}>
            {coupleDetailLoading ? (
              <div className="modal-loading"><div className="spinner" /></div>
            ) : coupleDetail && (
              <>
                <div className="modal-header">
                  <h2><Heart size={20} /> {coupleDetail.first_name} {coupleDetail.last_name}</h2>
                  <button className="modal-close" onClick={() => setCoupleDetail(null)}><X size={20} /></button>
                </div>
                <div className="modal-body">
                  <div className="vendor-detail-grid">
                    <div className="detail-section">
                      <h3>Account Info</h3>
                      <div className="detail-row"><Mail size={14} /> <span>{coupleDetail.email}</span></div>
                      <div className="detail-row"><Phone size={14} /> <span>{coupleDetail.phone || 'N/A'}</span></div>
                      <div className="detail-row"><Calendar size={14} /> <span>Joined: {formatDate(coupleDetail.created_at)}</span></div>
                    </div>
                    {coupleDetail.wedding && (
                      <div className="detail-section">
                        <h3>Wedding Details</h3>
                        <div className="detail-row"><Heart size={14} /> <span>{coupleDetail.wedding.partner1_name} & {coupleDetail.wedding.partner2_name}</span></div>
                        <div className="detail-row"><Calendar size={14} /> <span>Date: {formatDate(coupleDetail.wedding.wedding_date)}</span></div>
                        <div className="detail-row"><MapPin size={14} /> <span>{coupleDetail.wedding.venue_location || 'TBD'}, {coupleDetail.wedding.city}</span></div>
                        <div className="detail-row"><Users size={14} /> <span>Guests: {coupleDetail.wedding.guest_count}</span></div>
                        <div className="detail-row"><DollarSign size={14} /> <span>Budget: {formatNPR(coupleDetail.wedding.budget_npr)}</span></div>
                        <div className="detail-row"><span className={`approval-badge ${coupleDetail.wedding.status}`}>{coupleDetail.wedding.wedding_style} - {coupleDetail.wedding.status}</span></div>
                      </div>
                    )}
                  </div>

                  {!coupleDetail.wedding && (
                    <div className="detail-section"><p className="no-data-text">No wedding created yet</p></div>
                  )}

                  {coupleDetail.checklist && coupleDetail.checklist.length > 0 && (
                    <div className="detail-section">
                      <h3><CheckCircle size={16} /> Checklist ({coupleDetail.checklist.filter(c => c.is_completed).length}/{coupleDetail.checklist.length} completed)</h3>
                      <div className="detail-list compact">
                        {coupleDetail.checklist.map((item) => (
                          <div key={item.id} className={`detail-list-item compact ${item.is_completed ? 'completed' : ''}`}>
                            <span className={`checklist-indicator ${item.is_completed ? 'done' : ''}`}>
                              {item.is_completed ? <CheckCircle size={14} /> : <Clock size={14} />}
                            </span>
                            <span>{item.title}</span>
                            {item.due_date && <small className="due-date">{formatDate(item.due_date)}</small>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {coupleDetail.guests && coupleDetail.guests.length > 0 && (
                    <div className="detail-section">
                      <h3><Users size={16} /> Guest List ({coupleDetail.guests.length} guests)</h3>
                      <div className="couple-stats-row">
                        <div className="mini-stat"><span className="mini-stat-num">{coupleDetail.guests.filter(g => g.rsvp_status === 'accepted').length}</span><span className="mini-stat-label">Accepted</span></div>
                        <div className="mini-stat"><span className="mini-stat-num">{coupleDetail.guests.filter(g => g.rsvp_status === 'pending').length}</span><span className="mini-stat-label">Pending</span></div>
                        <div className="mini-stat"><span className="mini-stat-num">{coupleDetail.guests.filter(g => g.rsvp_status === 'declined').length}</span><span className="mini-stat-label">Declined</span></div>
                      </div>
                    </div>
                  )}

                  {coupleDetail.budgetItems && coupleDetail.budgetItems.length > 0 && (
                    <div className="detail-section">
                      <h3><DollarSign size={16} /> Budget ({coupleDetail.budgetItems.length} items)</h3>
                      <div className="couple-stats-row">
                        <div className="mini-stat"><span className="mini-stat-num">{formatNPR(coupleDetail.budgetItems.reduce((sum, b) => sum + Number(b.estimated_cost_npr || 0), 0))}</span><span className="mini-stat-label">Estimated</span></div>
                        <div className="mini-stat"><span className="mini-stat-num">{formatNPR(coupleDetail.budgetItems.reduce((sum, b) => sum + Number(b.actual_cost_npr || 0), 0))}</span><span className="mini-stat-label">Actual</span></div>
                        <div className="mini-stat"><span className="mini-stat-num">{coupleDetail.budgetItems.filter(b => b.is_paid).length}/{coupleDetail.budgetItems.length}</span><span className="mini-stat-label">Paid</span></div>
                      </div>
                    </div>
                  )}

                  {coupleDetail.reviews && coupleDetail.reviews.length > 0 && (
                    <div className="detail-section">
                      <h3><Star size={16} /> Reviews Written ({coupleDetail.reviews.length})</h3>
                      <div className="detail-list">
                        {coupleDetail.reviews.map((r) => (
                          <div key={r.id} className="detail-list-item">
                            <div className="detail-list-header">
                              <strong>{r.vendor_name}</strong>
                              <span className="review-rating"><Star size={12} className="star-filled" /> {r.rating}/5</span>
                            </div>
                            {r.title && <p className="detail-list-title">{r.title}</p>}
                            {r.body && <p className="detail-list-body">{r.body}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* BIDDING REQUESTS TAB */}
      {activeTab === 'bidding-requests' && (
        <div className="admin-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <Gavel size={18} /> Vendor Bidding Access Requests
          </h3>
          {tabLoading ? (
            <div className="tab-loading"><div className="spinner" /></div>
          ) : biddingRequests.length === 0 ? (
            <div className="portal-empty-tab" style={{ padding: '48px 24px', textAlign: 'center' }}>
              <Gavel size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
              <p style={{ color: '#6b7280' }}>No bidding access requests yet.</p>
            </div>
          ) : (
            <div className="admin-vendor-table">
              <table>
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Category</th>
                    <th>City</th>
                    <th>Owner</th>
                    <th>Requested</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {biddingRequests.map(r => (
                    <tr key={r.id}>
                      <td>
                        <div className="vendor-name-cell">
                          <strong>{r.business_name}</strong>
                          {r.is_verified && <CheckCircle size={12} style={{ color: '#16a34a', marginLeft: '4px' }} />}
                        </div>
                      </td>
                      <td>{r.category_name || '—'}</td>
                      <td>{r.city || '—'}</td>
                      <td>
                        <div className="vendor-name-cell">
                          <span>{r.owner_first_name} {r.owner_last_name}</span>
                          <small>{r.owner_email}</small>
                        </div>
                      </td>
                      <td>{r.bidding_requested_at ? new Date(r.bidding_requested_at).toLocaleDateString() : '—'}</td>
                      <td>
                        <span className={`approval-badge ${r.bidding_approved === 'approved' ? 'approved' : r.bidding_approved === 'denied' ? 'rejected' : 'pending'}`}>
                          {r.bidding_approved}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {r.bidding_approved === 'pending' && (
                            <>
                              <button
                                className="action-btn approve"
                                title="Approve bidding access"
                                disabled={biddingActionLoading === r.id}
                                onClick={async () => {
                                  setBiddingActionLoading(r.id);
                                  try {
                                    await approveBiddingRequest(r.id);
                                    const res = await getAdminBiddingRequests();
                                    setBiddingRequests(res.data);
                                  } catch (err) {
                                    alert('Failed to approve: ' + (err.response?.data?.error || err.message));
                                  } finally { setBiddingActionLoading(null); }
                                }}
                              >
                                <CheckCircle size={16} />
                              </button>
                              <button
                                className="action-btn reject"
                                title="Deny bidding access"
                                disabled={biddingActionLoading === r.id}
                                onClick={async () => {
                                  if (!await confirmDialog({ title: 'Deny Bidding', message: `Deny bidding access for ${r.business_name}?`, danger: true, confirmText: 'Deny' })) return;
                                  setBiddingActionLoading(r.id);
                                  try {
                                    await denyBiddingRequest(r.id);
                                    const res = await getAdminBiddingRequests();
                                    setBiddingRequests(res.data);
                                  } catch (err) {
                                    alert('Failed to deny: ' + (err.response?.data?.error || err.message));
                                  } finally { setBiddingActionLoading(null); }
                                }}
                              >
                                <XCircle size={16} />
                              </button>
                            </>
                          )}
                          {r.bidding_approved === 'approved' && (
                            <button
                              className="action-btn reject"
                              title="Revoke bidding access"
                              disabled={biddingActionLoading === r.id}
                              onClick={async () => {
                                if (!await confirmDialog({ title: 'Revoke Bidding', message: `Revoke bidding access for ${r.business_name}?`, danger: true, confirmText: 'Revoke' })) return;
                                setBiddingActionLoading(r.id);
                                try {
                                  await revokeBiddingAccess(r.id);
                                  const res = await getAdminBiddingRequests();
                                  setBiddingRequests(res.data);
                                } catch (err) {
                                  alert('Failed to revoke: ' + (err.response?.data?.error || err.message));
                                } finally { setBiddingActionLoading(null); }
                              }}
                            >
                              <Ban size={16} />
                            </button>
                          )}
                          {r.bidding_approved === 'denied' && (
                            <button
                              className="action-btn approve"
                              title="Approve bidding access"
                              disabled={biddingActionLoading === r.id}
                              onClick={async () => {
                                setBiddingActionLoading(r.id);
                                try {
                                  await approveBiddingRequest(r.id);
                                  const res = await getAdminBiddingRequests();
                                  setBiddingRequests(res.data);
                                } catch (err) {
                                  alert('Failed to approve: ' + (err.response?.data?.error || err.message));
                                } finally { setBiddingActionLoading(null); }
                              }}
                            >
                              <CheckCircle size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* INVITATIONS TAB */}
      {activeTab === 'invitations' && (
        <div className="admin-section">
          <h3 style={{ marginBottom: '8px' }}>Wedding Invitations Overview</h3>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>All digital invitations sent by couples across the platform.</p>

          {/* Stats Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            {[
              { label: 'Total', value: adminInvitationStats.total || 0, color: '#6B7280' },
              { label: 'Sent', value: adminInvitationStats.sent || 0, color: '#3B82F6' },
              { label: 'Accepted', value: adminInvitationStats.accepted || 0, color: '#22C55E' },
              { label: 'Declined', value: adminInvitationStats.declined || 0, color: '#EF4444' },
              { label: 'Pending RSVP', value: adminInvitationStats.pending_rsvp || 0, color: '#F59E0B' },
              { label: 'Weddings Using', value: adminInvitationStats.weddings_using || 0, color: '#be185d' },
            ].map(s => (
              <div key={s.label} style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {tabLoading ? <div className="loading-page"><div className="spinner" /></div> : (
            adminInvitations.length === 0 ? (
              <div className="empty-state" style={{ marginTop: '24px' }}>
                <Mail size={48} style={{ color: '#ccc', marginBottom: '12px' }} />
                <p>No invitations have been sent yet.</p>
              </div>
            ) : (
              <div className="admin-vendor-table">
                <table>
                  <thead>
                    <tr>
                      <th>Couple</th>
                      <th>Recipient</th>
                      <th>Email Status</th>
                      <th>RSVP</th>
                      <th>Sent</th>
                      <th>RSVP Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminInvitations.filter(inv => matchesSearch(inv, ['recipient_name', 'recipient_email', 'couple_first_name', 'couple_last_name', 'partner1_name', 'partner2_name'])).map(inv => (
                      <tr key={inv.id}>
                        <td>
                          {inv.partner1_name} & {inv.partner2_name}
                          <br /><small style={{ color: '#6b7280' }}>{inv.couple_first_name} {inv.couple_last_name}</small>
                        </td>
                        <td>
                          {inv.recipient_name}
                          <br /><small style={{ color: '#6b7280' }}>{inv.recipient_email}</small>
                        </td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                            background: inv.email_status === 'sent' ? '#DBEAFE' : inv.email_status === 'draft' ? '#F3F4F6' : inv.email_status === 'failed' ? '#FEE2E2' : '#DCFCE7',
                            color: inv.email_status === 'sent' ? '#1E40AF' : inv.email_status === 'draft' ? '#6B7280' : inv.email_status === 'failed' ? '#B91C1C' : '#166534',
                          }}>
                            {inv.email_status}
                          </span>
                          {inv.opened_at && <Eye size={12} style={{ marginLeft: '4px', color: '#3B82F6' }} title={`Opened: ${new Date(inv.opened_at).toLocaleString()}`} />}
                        </td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                            background: inv.rsvp_status === 'accepted' ? '#DCFCE7' : inv.rsvp_status === 'declined' ? '#FEE2E2' : inv.rsvp_status === 'maybe' ? '#DBEAFE' : '#FEF3C7',
                            color: inv.rsvp_status === 'accepted' ? '#166534' : inv.rsvp_status === 'declined' ? '#B91C1C' : inv.rsvp_status === 'maybe' ? '#1E40AF' : '#92400E',
                          }}>
                            {inv.rsvp_status}{inv.rsvp_guest_count > 1 ? ` (${inv.rsvp_guest_count})` : ''}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px', color: '#6b7280' }}>{inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : '—'}</td>
                        <td style={{ fontSize: '12px', color: '#6b7280' }}>{inv.rsvp_responded_at ? new Date(inv.rsvp_responded_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {/* SMS LOGS TAB */}
      {activeTab === 'sms-logs' && <SmsLogsPanel />}

      {/* SETTINGS TAB */}
      {activeTab === 'settings' && <AdminSettingsPanel />}

      {/* HERO IMAGES TAB */}
      {activeTab === 'hero-images' && <HeroImagesPanel />}

      {/* CATEGORIES TAB */}
      {activeTab === 'categories' && (
        <div className="admin-section">
          <h3 style={{ marginBottom: '16px' }}>Vendor Category Thumbnails</h3>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>Upload custom thumbnail images for each vendor category.</p>
          {tabLoading ? <div className="loading-page"><div className="spinner" /></div> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
              {categoriesList.map(cat => (
                <div key={cat.id} className="portal-info-card" style={{ padding: '0', overflow: 'hidden' }}>
                  <div style={{ height: '150px', background: '#f3f4f6', position: 'relative' }}>
                    {cat.thumbnail_url ? (
                      <img src={cat.thumbnail_url.startsWith('/') ? (process.env.REACT_APP_API_URL || '').replace('/api', '') + cat.thumbnail_url : cat.thumbnail_url} alt={cat.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>No image</div>
                    )}
                  </div>
                  <div style={{ padding: '12px' }}>
                    <h4 style={{ margin: '0 0 4px', fontSize: '14px' }}>{cat.name}</h4>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{cat.vendor_count} vendors • {cat.slug}</span>
                    <div style={{ marginTop: '10px' }}>
                      <input type="file" accept="image/*" id={`cat-thumb-${cat.id}`} style={{ display: 'none' }}
                        onChange={async (e) => {
                          const rawFile = e.target.files[0];
                          if (!rawFile) return;
                          const file = await compressImage(rawFile);
                          const formData = new FormData();
                          formData.append('thumbnail', file);
                          try {
                            await updateCategoryThumbnail(cat.id, formData);
                            const res = await getAdminCategories();
                            setCategoriesList(res.data);
                          } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
                        }} />
                      <button className="btn btn-outline" style={{ fontSize: '12px', padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        onClick={() => document.getElementById(`cat-thumb-${cat.id}`).click()}>
                        <Upload size={12} /> {cat.thumbnail_url ? 'Change' : 'Upload'} Thumbnail
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAMILY REQUESTS TAB */}
      {activeTab === 'family-requests' && (
        <div className="admin-section">
          <h3 style={{ marginBottom: '16px' }}>Family Photo Limit Requests</h3>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>Couples can request to increase their family member photo limit beyond the default 20.</p>
          {tabLoading ? <div className="loading-page"><div className="spinner" /></div> : (
            familyRequests.length === 0 ? (
              <div className="empty-state" style={{ marginTop: '24px' }}>
                <Users size={48} style={{ color: '#ccc', marginBottom: '12px' }} />
                <p>No family photo limit requests yet.</p>
              </div>
            ) : (
              <div className="admin-vendor-table">
                <table>
                  <thead>
                    <tr>
                      <th>Couple</th>
                      <th>Wedding</th>
                      <th>Current Limit</th>
                      <th>Requested</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {familyRequests.map(req => (
                      <tr key={req.id}>
                        <td>{req.first_name} {req.last_name}<br/><small style={{ color: '#6b7280' }}>{req.email}</small></td>
                        <td>{req.partner1_name} & {req.partner2_name}</td>
                        <td>{req.current_limit}</td>
                        <td><strong>{req.requested_limit}</strong></td>
                        <td>
                          <span className={`approval-badge ${req.status}`}>
                            {req.status === 'pending' && <Clock size={12} />}
                            {req.status === 'approved' && <CheckCircle size={12} />}
                            {req.status === 'rejected' && <XCircle size={12} />}
                            {req.status}
                          </span>
                        </td>
                        <td>
                          {req.status === 'pending' && (
                            <div className="action-buttons">
                              <button className="action-btn approve" onClick={async () => {
                                try {
                                  await approveFamilyRequest(req.id);
                                  const res = await getAdminFamilyRequests();
                                  setFamilyRequests(res.data);
                                } catch (err) { alert('Failed to approve: ' + (err.response?.data?.error || err.message)); }
                              }} title="Approve"><CheckCircle size={16} /></button>
                              <button className="action-btn reject" onClick={async () => {
                                const note = await promptDialog({ title: 'Reject Request', message: 'Rejection reason (optional):', placeholder: 'Enter reason...' });
                                if (note === null) return;
                                try {
                                  await rejectFamilyRequest(req.id, { admin_note: note });
                                  const res = await getAdminFamilyRequests();
                                  setFamilyRequests(res.data);
                                } catch (err) { alert('Failed to reject: ' + (err.response?.data?.error || err.message)); }
                              }} title="Reject"><XCircle size={16} /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {/* NEWSLETTER TAB */}
      {activeTab === 'newsletter' && (
        <div className="admin-section" style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <h3 style={{ marginBottom: '4px' }}>Vendor Email Newsletter</h3>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
            All vendors in NepWed. Send bulk or individual newsletters to vendor contacts.
          </p>

          {/* Compose Panel */}
          <div className="portal-info-card full-width" style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '12px' }}>Compose Newsletter</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="text"
                placeholder="Subject line..."
                value={newsletterSubject}
                onChange={(e) => setNewsletterSubject(e.target.value)}
                style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
              />
              <textarea
                rows={5}
                placeholder="Email body (plain text or HTML)..."
                value={newsletterBody}
                onChange={(e) => setNewsletterBody(e.target.value)}
                style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSendNewsletter(false)}
                  disabled={newsletterSending || !selectedLeads.size}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <Mail size={15} /> {newsletterSending ? 'Sending...' : `Send to Selected (${selectedLeads.size})`}
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => handleSendNewsletter(true)}
                  disabled={newsletterSending}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <Mail size={15} /> Send to All with Email ({vendorLeads.filter(l => l.email).length})
                </button>
                {newsletterResult && (
                  <span style={{ fontSize: '14px', fontWeight: 600, color: (newsletterResult.error || newsletterResult.failed > 0 || !newsletterResult.sent) ? '#dc2626' : '#16a34a' }}>
                    {newsletterResult.error || `${newsletterResult.failed > 0 || !newsletterResult.sent ? '⚠' : '✓'} ${newsletterResult.message}${newsletterResult.errors?.[0] ? ` (first error: ${newsletterResult.errors[0].error})` : ''}`}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Leads filter + table */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div className="admin-search" style={{ flex: '1', minWidth: '200px' }}>
              <Search size={16} />
              <input type="text" placeholder="Filter by email, name, category or city..." value={leadsFilter} onChange={(e) => setLeadsFilter(e.target.value)} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={verifiedOnly} onChange={async (e) => { setVerifiedOnly(e.target.checked); const res = await getVendorLeads({ verified_only: e.target.checked }); setVendorLeads(res.data.vendors || res.data); setVendorLeadsEmailCount(res.data.withEmail || 0); }} />
              Verified only
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={emailOnly} onChange={(e) => setEmailOnly(e.target.checked)} />
              Has email only
            </label>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{vendorLeads.length} vendors ({vendorLeadsEmailCount || vendorLeads.filter(l => l.email).length} with email)</span>
            <button className="btn btn-outline" style={{ fontSize: '13px', padding: '8px 14px' }} onClick={async () => { const res = await getVendorLeads({ verified_only: verifiedOnly }); setVendorLeads(res.data.vendors || res.data); setVendorLeadsEmailCount(res.data.withEmail || 0); }}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {tabLoading ? (
            <div className="tab-loading"><div className="spinner" /></div>
          ) : (() => {
            const filtered = vendorLeads.filter(l => {
              if (emailOnly && !l.email) return false;
              if (!leadsFilter) return true;
              const q = leadsFilter.toLowerCase();
              return [l.email, l.business_name, l.category, l.city].some(f => f && f.toLowerCase().includes(q));
            });
            return (
              <div className="admin-vendor-table">
                {selectedLeads.size > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', marginBottom: '12px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#1D4ED8' }}>{selectedLeads.size} selected</span>
                    <button className="btn btn-outline" style={{ fontSize: '13px', padding: '6px 12px' }} onClick={() => setSelectedLeads(new Set())}>Clear</button>
                  </div>
                )}
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input type="checkbox" onChange={() => toggleSelectAllLeads(filtered)} checked={selectedLeads.size === filtered.length && filtered.length > 0} style={{ cursor: 'pointer' }} />
                      </th>
                      <th>Email</th>
                      <th>Business Name</th>
                      <th>Category</th>
                      <th>City</th>
                      <th>Source</th>
                      <th>Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(lead => (
                      <tr key={lead.id} style={selectedLeads.has(lead.id) ? { background: '#EFF6FF' } : {}}>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={selectedLeads.has(lead.id)} onChange={() => toggleLeadSelect(lead.id)} style={{ cursor: 'pointer' }} />
                        </td>
                        <td>{lead.email || <span style={{ color: '#9ca3af', fontSize: '12px' }}>No email</span>}</td>
                        <td>{lead.business_name || '—'}</td>
                        <td>{lead.category || '—'}</td>
                        <td>{lead.city || '—'}</td>
                        <td><span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: lead.source === 'google' ? '#FEF3C7' : '#DBEAFE', color: lead.source === 'google' ? '#92400E' : '#1E40AF' }}>{lead.source || 'manual'}</span></td>
                        <td>{lead.is_verified ? <CheckCircle size={16} style={{ color: '#16a34a' }} /> : <XCircle size={16} style={{ color: '#d1d5db' }} />}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan="7" className="no-data">
                        {vendorLeads.length === 0
                          ? 'No vendors found. Sync Google Places vendors to populate this list.'
                          : 'No vendors match your filter.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}
      </div>
      {ConfirmDialog}
      {PromptDialog}
    </div>
  );
}

/* SMS Logs panel */
function SmsLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [error, setError] = useState('');

  // Through the shared client, which sends the stored session token. This used
  // to read localStorage 'token', which is never set, so every call was rejected.
  const load = useCallback((p = 1) => {
    setLoading(true);
    setError('');
    api.get('/admin/sms-logs', { params: { page: p, limit } })
      .then(res => { setLogs(res.data.logs || []); setTotal(res.data.total || 0); })
      .catch(err => setError(err.response?.data?.error || 'Could not load SMS logs.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(page); }, [load, page]);

  const statusColor = s => s === 'sent' ? '#22C55E' : s === 'failed' ? '#EF4444' : '#F59E0B';
  const triggerLabel = t => ({ rsvp: 'RSVP', new_inquiry: 'New Inquiry', inquiry_reply: 'Inquiry Reply' }[t] || t || '—');
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="admin-section">
      <h3 style={{ marginBottom: '4px' }}>SMS Logs</h3>
      <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
        All SMS messages sent via Twilio — {total} total entries
      </p>
      {loading ? (
        <div className="loading-page"><div className="spinner" /></div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px' }}>
          <MessageCircle size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
          <p>No SMS messages logged yet.</p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  {['Date', 'To', 'Trigger', 'Status', 'Message', 'Twilio SID / Error'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{log.to_number}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ background: '#ede9fe', color: '#6d28d9', borderRadius: '999px', padding: '2px 10px', fontSize: '11px', fontWeight: 600 }}>
                        {triggerLabel(log.trigger)}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ background: statusColor(log.status) + '20', color: statusColor(log.status), borderRadius: '999px', padding: '2px 10px', fontSize: '11px', fontWeight: 700 }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', maxWidth: '300px', color: '#374151' }}>{log.body}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '11px', color: log.error ? '#EF4444' : '#9ca3af', maxWidth: '200px', wordBreak: 'break-all' }}>
                      {log.twilio_sid || log.error || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
              <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={{ lineHeight: '36px', fontSize: '13px', color: '#6b7280' }}>Page {page} of {totalPages}</span>
              <button className="btn btn-outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* Separated settings panel to keep state self-contained */
function AdminSettingsPanel() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    getAdminSettings()
      .then(res => { setSettings(res.data); setLoading(false); })
      .catch(err => { setLoadError(err.response?.data?.error || err.message); setLoading(false); });
  }, []);

  // Keys that are on until switched off (the server and pages test !== 'false').
  // A key never saved has no row, so a flip has to start from "on" for these;
  // otherwise the first click wrote 'true' and the switch did not move.
  const toggleSetting = (key) => {
    const defaultOn = ['wedding_animation_enabled', 'stats_section_enabled', 'email_notifications_couples', 'email_notifications_vendors', 'vendor_reply_enabled', 'realtime_chat_enabled', 'website_bg_enabled'];
    setSettings(prev => {
      const on = defaultOn.includes(key) ? prev[key] !== 'false' : prev[key] === 'true';
      return { ...prev, [key]: on ? 'false' : 'true' };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await updateAdminSettings(settings);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tab-loading"><div className="spinner"></div></div>;
  // Without the stored values every switch would show its default, not the live state
  if (loadError) return <div className="admin-section"><div className="alert alert-error">Could not load the current settings: {loadError}. Reload the page to try again.</div></div>;

  return (
    <div className="admin-section" style={{ display: 'flex', justifyContent: 'center' }}>
      <div className="portal-info-card" style={{ maxWidth: 600, width: '100%' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShieldCheck size={18} /> App Settings
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Wedding Animation (Home Page)</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Show animated bride & groom beside the hero title on the front page</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={settings.wedding_animation_enabled !== 'false'}
                onChange={() => toggleSetting('wedding_animation_enabled')}
                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s',
                background: settings.wedding_animation_enabled !== 'false' ? '#22C55E' : '#ccc',
              }}>
                <span style={{
                  position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white',
                  left: settings.wedding_animation_enabled !== 'false' ? '26px' : '3px', top: '3px', transition: 'all 0.3s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Stats Banner (Home Page)</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Show the 500+ Vendors / 100+ Weddings / 50+ Cities / 4.8★ stats section on the home page</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={settings.stats_section_enabled !== 'false'}
                onChange={() => toggleSetting('stats_section_enabled')}
                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s',
                background: settings.stats_section_enabled !== 'false' ? '#22C55E' : '#ccc',
              }}>
                <span style={{
                  position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white',
                  left: settings.stats_section_enabled !== 'false' ? '26px' : '3px', top: '3px', transition: 'all 0.3s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Email Verification Required</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Always on: new users must verify their email before logging in. This switch is not connected yet, so turning it off would change nothing.</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input
                type="checkbox"
                checked
                readOnly
                disabled
                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
              />
              <span style={{
                position: 'absolute', cursor: 'not-allowed', inset: 0, borderRadius: '28px', transition: 'all 0.3s',
                background: '#22C55E', opacity: 0.6,
              }}>
                <span style={{
                  position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white',
                  left: '26px', top: '3px', transition: 'all 0.3s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </span>
            </label>
          </div>

          {/* Email Notifications */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Email Notifications (Global)</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Master switch for the inquiry emails below (off unless switched on). Check delivery with Email SMTP Test on the Google Sync tab.</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input type="checkbox" checked={settings.email_notifications_enabled === 'true'} onChange={() => toggleSetting('email_notifications_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.email_notifications_enabled === 'true' ? '#22C55E' : '#ccc' }}>
                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.email_notifications_enabled === 'true' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </span>
            </label>
          </div>

          {settings.email_notifications_enabled === 'true' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f0fdf4', borderRadius: '10px', marginLeft: '20px' }}>
                <div>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Notify Couples</strong>
                  <span style={{ fontSize: '13px', color: '#666' }}>Email couples when a vendor replies or sends a chat message (not while they have that conversation open)</span>
                </div>
                <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
                  <input type="checkbox" checked={settings.email_notifications_couples !== 'false'} onChange={() => toggleSetting('email_notifications_couples')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                  <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.email_notifications_couples !== 'false' ? '#22C55E' : '#ccc' }}>
                    <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.email_notifications_couples !== 'false' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </span>
                </label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f0fdf4', borderRadius: '10px', marginLeft: '20px' }}>
                <div>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Notify Vendors</strong>
                  <span style={{ fontSize: '13px', color: '#666' }}>Email vendors when a couple sends a new inquiry (later chat messages are not emailed)</span>
                </div>
                <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
                  <input type="checkbox" checked={settings.email_notifications_vendors !== 'false'} onChange={() => toggleSetting('email_notifications_vendors')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                  <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.email_notifications_vendors !== 'false' ? '#22C55E' : '#ccc' }}>
                    <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.email_notifications_vendors !== 'false' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </span>
                </label>
              </div>
            </>
          )}

          {/* SMS Notifications */}
          <div style={{ padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ display: 'block', marginBottom: '4px' }}>SMS Notifications</strong>
                <span style={{ fontSize: '13px', color: '#666' }}>Send SMS alerts — RSVP confirmations, inquiry alerts, and vendor replies. Needs Twilio credentials on the server; without them messages are only logged (status "dev" in SMS Logs).</span>
              </div>
              <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
                <input type="checkbox" checked={settings.sms_notifications_enabled === 'true'} onChange={() => toggleSetting('sms_notifications_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.sms_notifications_enabled === 'true' ? '#22C55E' : '#ccc' }}>
                  <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.sms_notifications_enabled === 'true' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </span>
              </label>
            </div>

          </div>

          {/* Bidding Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Vendor Bidding Marketplace</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Enable the bidding page where vendors post service listings and couples place bids</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input type="checkbox" checked={settings.bidding_enabled === 'true'} onChange={() => toggleSetting('bidding_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.bidding_enabled === 'true' ? '#22C55E' : '#ccc' }}>
                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.bidding_enabled === 'true' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </span>
            </label>
          </div>

          {/* Wedding Videos Page Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Wedding Videos Page</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Show the Wedding Videos page in navigation with Nepali wedding videos from YouTube</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input type="checkbox" checked={settings.videos_page_enabled === 'true'} onChange={() => toggleSetting('videos_page_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.videos_page_enabled === 'true' ? '#22C55E' : '#ccc' }}>
                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.videos_page_enabled === 'true' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </span>
            </label>
          </div>

          {/* Reply Feature Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Vendor Reply to Inquiries</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Allow vendors to reply to couple inquiries from their portal</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input type="checkbox" checked={settings.vendor_reply_enabled !== 'false'} onChange={() => toggleSetting('vendor_reply_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.vendor_reply_enabled !== 'false' ? '#22C55E' : '#ccc' }}>
                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.vendor_reply_enabled !== 'false' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </span>
            </label>
          </div>

          {/* Vendor-to-Vendor Communication Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Vendor-to-Vendor Messaging</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Allow vendors to send messages to other vendors on the platform</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input type="checkbox" checked={settings.vendor_to_vendor_enabled === 'true'} onChange={() => toggleSetting('vendor_to_vendor_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.vendor_to_vendor_enabled === 'true' ? '#22C55E' : '#ccc' }}>
                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.vendor_to_vendor_enabled === 'true' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </span>
            </label>
          </div>

          {/* Real-Time Chat Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Real-Time Chat (Socket.IO)</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Enable instant message delivery via WebSocket. When off, messages use 15-second polling instead.</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input type="checkbox" checked={settings.realtime_chat_enabled !== 'false'} onChange={() => toggleSetting('realtime_chat_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.realtime_chat_enabled !== 'false' ? '#22C55E' : '#ccc' }}>
                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.realtime_chat_enabled !== 'false' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </span>
            </label>
          </div>

          {/* Wedding Website Background Image Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8f8f8', borderRadius: '10px' }}>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Wedding Website Background Images</strong>
              <span style={{ fontSize: '13px', color: '#666' }}>Show couples' custom background images on their public wedding website pages (when off, uploads are kept but not shown)</span>
            </div>
            <label style={{ position: 'relative', width: '52px', height: '28px', flexShrink: 0 }}>
              <input type="checkbox" checked={settings.website_bg_enabled !== 'false'} onChange={() => toggleSetting('website_bg_enabled')} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
              <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: '28px', transition: 'all 0.3s', background: settings.website_bg_enabled !== 'false' ? '#22C55E' : '#ccc' }}>
                <span style={{ position: 'absolute', content: '""', height: '22px', width: '22px', borderRadius: '50%', background: 'white', left: settings.website_bg_enabled !== 'false' ? '26px' : '3px', top: '3px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </span>
            </label>
          </div>
        </div>

        <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          {message && (
            <span style={{ fontSize: '14px', color: message.includes('success') ? '#22C55E' : '#EF4444', fontWeight: 600 }}>
              {message}
            </span>
          )}
        </div>

      </div>
    </div>
  );
}

/* Hero Images Management Panel */
function HeroImagesPanel() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ title: '', highlight: '', subtitle: '', sort_order: 0, file: null, image_url: '', video_url: '', media_type: 'image' });
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [loadError, setLoadError] = useState('');

  const loadImages = () => {
    getAdminHeroImages()
      .then(res => { setImages(res.data); setLoadError(''); setLoading(false); })
      .catch(err => { setLoadError(err.response?.data?.error || err.message); setLoading(false); });
  };

  // The home page shows one active slide per sort order (the oldest), so a
  // slide sharing its number with an older active one never appears.
  const isShadowed = (img) => img.is_active && images.some(o =>
    o.id < img.id && o.is_active && (Number(o.sort_order) || 0) === (Number(img.sort_order) || 0));
  const nextSortOrder = () => images.reduce((m, i) => Math.max(m, Number(i.sort_order) || 0), -1) + 1;

  useEffect(() => { loadImages(); }, []);

  const resetForm = () => {
    setForm({ title: '', highlight: '', subtitle: '', sort_order: 0, file: null, image_url: '', video_url: '', media_type: 'image' });
    setEditing(null);
    setShowForm(false);
    setMessage('');
  };

  const startEdit = (img) => {
    setForm({ title: img.title || '', highlight: img.highlight || '', subtitle: img.subtitle || '', sort_order: img.sort_order || 0, file: null, image_url: img.image_url || '', video_url: img.video_url || '', media_type: img.media_type || 'image' });
    setEditing(img.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('highlight', form.highlight);
      fd.append('subtitle', form.subtitle);
      fd.append('sort_order', form.sort_order);
      fd.append('media_type', form.media_type);
      if (form.file) {
        if (form.media_type === 'video') {
          fd.append('media', form.file);
        } else {
          const compressedHero = await compressImage(form.file);
          fd.append('media', compressedHero);
        }
      } else if (form.media_type === 'video' && form.video_url) {
        fd.append('video_url', form.video_url);
      } else if (form.image_url) {
        fd.append('image_url', form.image_url);
      }
      if (editing) {
        await updateHeroImage(editing, fd);
        setMessage('Slide updated!');
      } else {
        await addHeroImage(fd);
        setMessage('Slide added!');
      }
      loadImages();
      setTimeout(resetForm, 1200);
    } catch (err) {
      setMessage('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog({ title: 'Delete Hero Image', message: 'Delete this hero image?', danger: true, confirmText: 'Delete' })) return;
    try {
      await deleteHeroImage(id);
      loadImages();
    } catch (err) {
      // message is only rendered inside the form, which is usually closed here
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleToggle = async (img) => {
    try {
      const fd = new FormData();
      fd.append('is_active', !img.is_active);
      await updateHeroImage(img.id, fd);
      loadImages();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <div className="tab-loading"><div className="spinner"></div></div>;

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
          <Image size={18} /> Hero Images ({images.length})
        </h3>
        <button className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => { resetForm(); setForm(p => ({ ...p, sort_order: nextSortOrder() })); setShowForm(true); }}>
          <Plus size={16} /> Add Image
        </button>
      </div>

      {showForm && (
        <div className="portal-info-card" style={{ marginBottom: '24px', maxWidth: 720 }}>
          <h4 style={{ marginBottom: '16px' }}>{editing ? 'Edit Hero Image' : 'Add New Hero Image'}</h4>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Title</label>
                <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Plan Your Dream" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Highlight Text</label>
                <input type="text" value={form.highlight} onChange={e => setForm(p => ({ ...p, highlight: e.target.value }))} placeholder="e.g. Nepali Wedding" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Subtitle</label>
              <input type="text" value={form.subtitle} onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))} placeholder="Description text" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Slide Media Type</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  <input type="radio" name="media_type" value="image" checked={form.media_type === 'image'} onChange={() => setForm(p => ({ ...p, media_type: 'image', file: null, video_url: '' }))} /> Image
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  <input type="radio" name="media_type" value="video" checked={form.media_type === 'video'} onChange={() => setForm(p => ({ ...p, media_type: 'video', file: null, image_url: '' }))} /> Video
                </label>
              </div>
            </div>
            {form.media_type === 'image' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Upload Image</label>
                  <div style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', background: '#fafafa', boxSizing: 'border-box' }}>
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setForm(p => ({ ...p, file: e.target.files[0] }))} style={{ fontSize: '13px', width: '100%' }} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Or Image URL</label>
                  <input type="text" value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://..." style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>YouTube URL or Video Link</label>
                  <input type="text" value={form.video_url} onChange={e => setForm(p => ({ ...p, video_url: e.target.value, file: null }))} placeholder="https://youtube.com/watch?v=... or video URL" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Or Upload Video File</label>
                  <div style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', background: '#fafafa', boxSizing: 'border-box' }}>
                    <input type="file" accept="video/mp4,video/webm,video/ogg" onChange={e => setForm(p => ({ ...p, file: e.target.files[0], video_url: '' }))} style={{ fontSize: '13px', width: '100%' }} />
                  </div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ width: '100px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ height: '42px' }}>
                {saving ? 'Saving...' : editing ? 'Update Image' : 'Add Image'}
              </button>
              <button type="button" className="btn btn-outline" onClick={resetForm} style={{ height: '42px' }}>Cancel</button>
              {message && <span style={{ fontSize: '14px', color: message.startsWith('Error') ? '#EF4444' : '#22C55E', fontWeight: 600, alignSelf: 'center' }}>{message}</span>}
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Give each slide its own sort order: the home page shows only one slide per number.</p>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
        {images.map((img, idx) => (
          <div key={img.id} className="portal-info-card" style={{ padding: '0', overflow: 'hidden', opacity: img.is_active ? 1 : 0.5 }}>
            <div style={{ position: 'relative', height: '180px', background: '#f0f0f0' }}>
              {img.media_type === 'video' ? (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e', color: '#fff', fontSize: '13px', gap: '8px' }}>
                  <span style={{ fontSize: '28px' }}>▶</span>
                  <span>{img.video_url ? (img.video_url.includes('youtube') ? 'YouTube Video' : 'Video File') : 'Video Slide'}</span>
                </div>
              ) : (
                <img
                  src={img.image_url && img.image_url.startsWith('/') ? (process.env.REACT_APP_API_URL || '').replace('/api', '') + img.image_url : img.image_url}
                  alt={img.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <span style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                Slide {idx + 1} {img.media_type === 'video' ? '🎬' : '🖼'}
              </span>
              {!img.is_active && (
                <span style={{ position: 'absolute', top: '8px', right: '8px', background: '#EF4444', color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                  Hidden
                </span>
              )}
              {isShadowed(img) && (
                <span style={{ position: 'absolute', bottom: '8px', left: '8px', background: '#f59e0b', color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                  Not shown: same sort order as an older slide
                </span>
              )}
            </div>
            <div style={{ padding: '16px' }}>
              <h4 style={{ margin: '0 0 4px', fontSize: '15px' }}>
                {img.title} <span style={{ color: 'var(--primary)' }}>{img.highlight}</span>
              </h4>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px', lineHeight: 1.4 }}>{img.subtitle || 'No subtitle'}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="btn btn-outline" style={{ fontSize: '12px', padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => startEdit(img)}>
                  <Edit size={12} /> Edit
                </button>
                <button className="btn btn-outline" style={{ fontSize: '12px', padding: '6px 12px', display: 'inline-flex', alignItems: 'center' }} onClick={() => handleToggle(img)}>
                  {img.is_active ? 'Hide' : 'Show'}
                </button>
                <button className="btn btn-outline" style={{ fontSize: '12px', padding: '6px 12px', color: '#EF4444', borderColor: '#EF4444', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleDelete(img.id)}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {loadError && <div className="alert alert-error" style={{ marginTop: '24px' }}>Could not load hero images: {loadError}</div>}

      {images.length === 0 && !loadError && (
        <div className="empty-state" style={{ marginTop: '24px' }}>
          <Image size={48} style={{ color: '#ccc', marginBottom: '12px' }} />
          <p>No hero images yet. Add images to display on the homepage slideshow.</p>
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
}

export default AdminDashboard;
